import React, { useState, useEffect } from "react";
import { Statistic } from 'antd';
import {
  Card,
  Typography,
  Table,
  Button,
  Space,
  Modal,
  Avatar,
  Input,
  message,
  Tag,
  Tooltip,
  Descriptions,
  Badge,
  Spin,
  Alert,
  Row,
  Col,
  Divider,
  Checkbox,
  Radio,
  Select,
  Form as AntForm,
  Tabs,
  Steps,
  Empty,
  DatePicker,
  TimePicker,
  Upload,
  Popover,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  HistoryOutlined,
  BankOutlined,
  UserOutlined,
  CalendarOutlined,
  ApiOutlined,
  FileTextOutlined,
  ArrowLeftOutlined,
  DesktopOutlined,
  AlertOutlined,
  EditOutlined,
  SafetyOutlined,
  SwapOutlined,
  ClockCircleOutlined,
  BarChartOutlined,
  UploadOutlined,
  DeleteOutlined,
  PlusOutlined,
  StarOutlined,
  NumberOutlined,
  FontSizeOutlined,
  BorderOutlined,
  CheckSquareOutlined,
  BellOutlined,
  CalculatorOutlined,
  CodeOutlined,
  FieldTimeOutlined,
  ColumnWidthOutlined,
  FontColorsOutlined,
  SearchOutlined,MessageOutlined,
  FunctionOutlined, InfoCircleOutlined, ShareAltOutlined, LockOutlined
} from "@ant-design/icons";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import Navbar from "./Navbar";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_BASE = `${API_BASE_URL}/api`;

const AcceptForms = () => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [reviewerNotes, setReviewerNotes] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [error, setError] = useState(null);
  const [userDesks, setUserDesks] = useState([]);
  const [deskFilter, setDeskFilter] = useState('all');
  const [deskFormsCount, setDeskFormsCount] = useState({});
  const [formAccessControlMap, setFormAccessControlMap] = useState({});
  const [submittedData, setSubmittedData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [language, setLanguage] = useState('english');
  const [previewActiveTab, setPreviewActiveTab] = useState('form-data');
  const [editableValues, setEditableValues] = useState({});
  const [editableFileList, setEditableFileList] = useState({});
  const navigate = useNavigate();
  const [allUlbs, setAllUlbs] = useState([]);
  const [ulbMap, setUlbMap] = useState({});
  const [showAllUlbs, setShowAllUlbs] = useState({});
  const [notifying, setNotifying] = useState(false);

  // ==================== HELPER FUNCTIONS FOR PREVIEW VALIDATION ====================
const isLogicalOperator = (operator) => {
  const logicalOps = ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_equal', 'less_than_equal'];
  return logicalOps.includes(operator);
};

  const getUnitDisplayInfo = (field) => {
    if (!field) return null;
    if (field.selectedUnitObject) {
      return { symbol: field.selectedUnitObject.unit_symbol, name: field.selectedUnitObject.unit_name, value: field.selectedUnitObject.unit_value };
    }
    if (field.units?.enabled && field.units.selectedUnitSymbol) {
      return { symbol: field.units.selectedUnitSymbol, name: field.units.selectedUnitLabel, value: field.units.selectedUnitValue };
    }
    if (field.selectedUnit && typeof field.selectedUnit === 'object') {
      return { symbol: field.selectedUnit.unit_symbol, name: field.selectedUnit.unit_name, value: field.selectedUnit.unit_value };
    }
    return null;
  };

  // ==================== COMPLETE TEXT VALIDATION WITH EXACT LENGTH ====================
const applyTextValidation = (field, value) => {
  const validation = field?.validation || {};
  let transformed = value === null || value === undefined ? '' : String(value);
  let error = null;
  const exactLength = validation.exactLength;

  if (!value) return { transformed: '', error: null };

  // ✅ EXACT LENGTH - Limit input length first
  if (exactLength && transformed.length > exactLength) {
    transformed = transformed.slice(0, exactLength);
  }

  // 1. SPACE VALIDATIONS
  if (validation.trimInput) transformed = transformed.trim();
  if (validation.collapseSpaces) transformed = transformed.replace(/\s+/g, ' ');
  if (validation.allowLeadingSpaces === false && transformed.startsWith(' ')) {
    transformed = transformed.replace(/^\s+/, '');
    error = "Leading spaces are not allowed";
  }
  if (validation.allowTrailingSpaces === false && transformed.endsWith(' ')) {
    transformed = transformed.replace(/\s+$/, '');
    error = "Trailing spaces are not allowed";
  }
  if (validation.allowSpaces === false && transformed.includes(' ')) {
    transformed = transformed.replace(/\s/g, '');
    error = "Spaces are not allowed";
  }

  // 2. CASE VALIDATIONS
  if (validation.uppercase) transformed = transformed.toUpperCase();
  if (validation.lowercase) transformed = transformed.toLowerCase();
  if (validation.capitalize && transformed.length > 0) {
    transformed = transformed.charAt(0).toUpperCase() + transformed.slice(1).toLowerCase();
  }
  if (validation.titleCase) {
    transformed = transformed.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  }

  // 3. LANGUAGE SCRIPT VALIDATION (Marathi/English Only)
  const languageScript = validation.languageScript || "any";
  const marathiRegex = /[\u0900-\u097F]/;
  const englishRegex = /[A-Za-z]/;

  if (languageScript === "marathi_only") {
    if (englishRegex.test(transformed)) {
      error = "✗ Only Marathi (देवनागरी) characters allowed";
      transformed = transformed.replace(/[A-Za-z]/g, '');
    }
    transformed = transformed.replace(/[^\u0900-\u097F\s]/g, '');
  }
  else if (languageScript === "english_only") {
    if (marathiRegex.test(transformed)) {
      error = "✗ Only English characters allowed";
      transformed = transformed.replace(/[\u0900-\u097F]/g, '');
    }
  }

  // 4. PATTERN VALIDATION
  if (validation.patternType && validation.patternType !== 'none' && transformed) {
    let pattern;
    switch (validation.patternType) {
      case 'letters_only': pattern = /^[A-Za-z\u0900-\u097F]+$/; break;
      case 'letters_spaces': pattern = /^[A-Za-z\u0900-\u097F\s]+$/; break;
      case 'alphanumeric': pattern = /^[A-Za-z0-9\u0900-\u097F]+$/; break;
      case 'alphanumeric_spaces': pattern = /^[A-Za-z0-9\u0900-\u097F\s]+$/; break;
      case 'numbers_only': pattern = /^[0-9]+$/; break;
      case 'email': pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; break;
      case 'phone': pattern = /^[0-9+\-\s()]+$/; break;
      case 'url':
        try { new URL(transformed); } catch(e) { error = "✗ Invalid URL format"; }
        break;
    }
    if (pattern && !pattern.test(transformed)) {
      error = `✗ Invalid ${validation.patternType.replace('_', ' ')} format`;
    }
  }

  return { transformed, error };
};

// ==================== COMPLETE NUMBER VALIDATION ====================
const applyNumberValidation = (field, value) => {
  const validation = field?.validation || {};
  let transformed = value === null || value === undefined ? '' : String(value);
  let error = null;
  const exactLength = validation.exactLength;
  const isIntegerOnly = validation.integerOnly === true;
  const allowDecimals = validation.allowDecimals !== false && !isIntegerOnly;
  const allowNegative = validation.allowNegative === true;

  if (!value && value !== 0) return { transformed: '', error: null };

  // 🔥 For integer-only fields: strip decimal points completely
  if (isIntegerOnly) {
    transformed = transformed.replace(/\./g, '');
    if (allowNegative) {
      transformed = transformed.replace(/[^-0-9]/g, '');
      if (transformed.indexOf('-') > 0) transformed = transformed.replace(/-/g, '');
      if (transformed === '-') transformed = '';
    } else {
      transformed = transformed.replace(/[^0-9]/g, '');
    }
    return { transformed: transformed === '' ? '' : transformed, error: null };
  }

  // 🔥 For decimal fields: ALLOW decimal point
  // 1. Remove spaces if not allowed
  if (validation.allowSpaces === false && typeof transformed === 'string') {
    transformed = transformed.replace(/\s/g, '');
  }
  if (validation.allowLeadingSpaces === false && typeof transformed === 'string' && transformed.startsWith(' ')) {
    transformed = transformed.trimStart();
  }
  if (validation.allowTrailingSpaces === false && typeof transformed === 'string' && transformed.endsWith(' ')) {
    transformed = transformed.trimEnd();
  }

  // 2. Filter characters based on settings
  if (typeof transformed === 'string') {
    if (allowNegative && allowDecimals) {
      transformed = transformed.replace(/[^-0-9.]/g, '');
    } else if (allowNegative && !allowDecimals) {
      transformed = transformed.replace(/[^-0-9]/g, '');
    } else if (!allowNegative && allowDecimals) {
      transformed = transformed.replace(/[^0-9.]/g, '');
    } else {
      transformed = transformed.replace(/[^0-9]/g, '');
    }
  }

  // 3. Handle decimal points
  if (allowDecimals && typeof transformed === 'string') {
    const decimalCount = (transformed.match(/\./g) || []).length;
    if (decimalCount > 1) {
      const firstIndex = transformed.indexOf('.');
      transformed = transformed.substring(0, firstIndex + 1) + 
                    transformed.substring(firstIndex + 1).replace(/\./g, '');
    }
  } else {
    transformed = transformed.replace(/\./g, '');
  }

  // 4. Handle negative sign
  if (typeof transformed === 'string') {
    if (!allowNegative && transformed.includes('-')) {
      error = "✗ Negative numbers are not allowed";
      transformed = transformed.replace(/-/g, '');
    } else if (allowNegative && transformed.includes('-')) {
      if (transformed.indexOf('-') !== 0) {
        transformed = '-' + transformed.replace(/-/g, '');
      }
      const negativeCount = (transformed.match(/-/g) || []).length;
      if (negativeCount > 1) {
        transformed = '-' + transformed.replace(/-/g, '');
      }
    }
  }

  // 5. Exact digit length validation
  if (exactLength && transformed && !error && typeof transformed === 'string') {
    const digitsOnly = transformed.replace(/[^0-9]/g, '');
    if (digitsOnly.length > exactLength) {
      const allowedDigits = digitsOnly.slice(0, exactLength);
      const hasNegative = transformed.startsWith('-');
      
      if (allowDecimals && transformed.includes('.')) {
        const decimalPart = transformed.slice(transformed.indexOf('.') + 1);
        const limitedDecimal = decimalPart.slice(0, validation.decimalPlaces || decimalPart.length);
        transformed = (hasNegative ? '-' : '') + allowedDigits + '.' + limitedDecimal;
      } else {
        transformed = (hasNegative ? '-' : '') + allowedDigits;
      }
    }
  }

  // 6. Apply decimal places limit and validate
  const numValue = parseFloat(transformed);
  if (!isNaN(numValue) && transformed !== '' && typeof transformed === 'string') {
    if (validation.allowZero === false && numValue === 0) {
      error = "✗ Zero is not allowed";
      transformed = '';
    }
    if (validation.decimalPlaces && validation.decimalPlaces > 0 && allowDecimals && transformed.includes('.')) {
      const parts = transformed.split('.');
      if (parts[1] && parts[1].length > validation.decimalPlaces) {
        transformed = numValue.toFixed(validation.decimalPlaces);
      }
    }
    if (!allowDecimals && transformed.includes('.')) {
      error = "✗ Decimals are not allowed";
      transformed = Math.floor(numValue).toString();
    }
  }

  return { transformed: transformed === '' ? '' : transformed, error };
};

  // ==================== COMPLETE DATE VALIDATION ====================
  const applyDateValidation = (field, value) => {
    const validation = field.validation || {};
    let error = null;

    if (!value) return { valid: true, error: null };

    const dateValue = new Date(value);
    if (isNaN(dateValue.getTime())) return { valid: false, error: "Invalid date" };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (validation.futureDates && dateValue < today) error = "Future dates only";
    if (validation.pastDates && dateValue > today) error = "Past dates only";
    if (validation.todayOnly && dateValue.toDateString() !== today.toDateString()) error = "Today's date only";
    if (validation.minDate && dateValue < new Date(validation.minDate)) error = `Date must be after ${validation.minDate}`;
    if (validation.maxDate && dateValue > new Date(validation.maxDate)) error = `Date must be before ${validation.maxDate}`;

    return { valid: !error, error };
  };

  // ==================== BUILD ACTIVE RULES FOR TEXT ====================
  const buildTextActiveRules = (field) => {
    const rules = [];
    const v = field.validation || {};

    if (v.exactLength) rules.push({ color: "orange", text: `📏 Exact: ${v.exactLength} chars` });
    if (v.patternType && v.patternType !== 'none') {
      const patternMap = {
        letters_only: "🔤 Letters Only",
        letters_spaces: "🔤 Letters + Spaces",
        alphanumeric: "🔢 Alphanumeric",
        alphanumeric_spaces: "🔢 Alphanumeric + Spaces",
        numbers_only: "🔢 Numbers Only",
        email: "📧 Email",
        phone: "📞 Phone",
        url: "🔗 URL",
      };
      rules.push({ color: "geekblue", text: patternMap[v.patternType] || v.patternType });
    }
    if (v.uppercase) rules.push({ color: "purple", text: "🔠 UPPERCASE" });
    if (v.lowercase) rules.push({ color: "purple", text: "🔡 lowercase" });
    if (v.capitalize) rules.push({ color: "cyan", text: "📝 Capitalize" });
    if (v.titleCase) rules.push({ color: "cyan", text: "📚 Title Case" });
    if (v.allowSpaces === false) rules.push({ color: "red", text: "🚫 No spaces" });
    if (v.allowLeadingSpaces === false) rules.push({ color: "red", text: "🚫 No leading spaces" });
    if (v.allowTrailingSpaces === false) rules.push({ color: "red", text: "🚫 No trailing spaces" });
    if (v.allowMultipleSpaces === false) rules.push({ color: "red", text: "🚫 No multiple spaces" });
    if (v.trimInput) rules.push({ color: "green", text: "✂️ Auto trim" });
    if (v.collapseSpaces) rules.push({ color: "green", text: "🔨 Collapse spaces" });
    if (v.languageScript === "marathi_only") rules.push({ color: "green", text: "🇮🇳 मराठी Only" });
    if (v.languageScript === "english_only") rules.push({ color: "blue", text: "🇬🇧 English Only" });

    return rules;
  };

// ==================== GET WORDS REPRESENTATION ====================
const getWordsRepresentation = (value) => {
  if (value === undefined || value === null || value === '') return '';
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return '';
  if (numValue === 0) return '';
  const words = numberToWords(numValue);
  if (words && words !== 'zero') {
    return words.charAt(0).toUpperCase() + words.slice(1);
  }
  return '';
};

  // ==================== BUILD ACTIVE RULES FOR NUMBER ====================
const buildNumberActiveRules = (field, unitInfo) => {
  const rules = [];
  const v = field.validation || {};

  if (unitInfo) rules.push({ color: "blue", text: `📊 Unit: ${unitInfo.symbol}` });
  if (v.exactLength) rules.push({ color: "orange", text: `📏 Exact: ${v.exactLength} digits` });
  if (v.decimalPlaces && v.decimalPlaces > 0) {
    rules.push({ color: "cyan", text: `🔢 ${v.decimalPlaces} decimal place(s)` });
  }
  if (v.allowDecimals === false || v.integerOnly === true) {
    rules.push({ color: "orange", text: "🚫 No decimals" });
  } else if (v.allowDecimals === true && v.decimalPlaces === null) {
    rules.push({ color: "green", text: "✓ Decimals allowed" });
  }
  if (v.allowNegative === false) {
    rules.push({ color: "orange", text: "➕ Positive only" });
  } else if (v.allowNegative === true) {
    rules.push({ color: "green", text: "✓ Negative allowed" });
  }
  if (v.integerOnly) {
    rules.push({ color: "purple", text: "🔢 Integer only (no decimals)" });
  }
  if (v.allowZero === false) {
    rules.push({ color: "red", text: "🚫 Zero not allowed" });
  }
  if (v.allowSpaces === false) rules.push({ color: "red", text: "🚫 No spaces" });
  if (v.allowLeadingSpaces === false) rules.push({ color: "red", text: "🚫 No leading spaces" });
  if (v.allowTrailingSpaces === false) rules.push({ color: "red", text: "🚫 No trailing spaces" });
  if (v.trimInput) rules.push({ color: "green", text: "✂️ Auto trim" });

  return rules;
};

  // ==================== BUILD DATE RULES ====================
  const buildDateRules = (field) => {
    const rules = [];
    const v = field.validation || {};

    if (v.minDate) rules.push({ color: "purple", text: `📅 Min: ${v.minDate}` });
    if (v.maxDate) rules.push({ color: "purple", text: `📅 Max: ${v.maxDate}` });
    if (v.futureDates) rules.push({ color: "cyan", text: "🚀 Future dates only" });
    if (v.pastDates) rules.push({ color: "cyan", text: "📜 Past dates only" });
    if (v.todayOnly) rules.push({ color: "green", text: "📆 Today only" });

    return rules;
  };

  // ==================== NUMBER TO WORDS CONVERSION ====================
const numberToWords = (num) => {
  if (num === '' || num === null || num === undefined) return '';
  
  const numStr = String(num).replace(/,/g, '');
  if (isNaN(numStr) || numStr === '') return '';
  
  const number = parseFloat(numStr);
  if (isNaN(number)) return '';
  
  const parts = numStr.split('.');
  const integerPart = parseInt(parts[0], 10);
  // ✅ FIX: Keep decimal part as STRING to preserve leading zeros
  const decimalPartStr = parts[1] || null;
  
  const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
    'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
  const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

  const convertLessThanThousand = (n) => {
    if (n === 0) return '';
    if (n < 20) return ones[n] + ' ';
    if (n < 100) return tens[Math.floor(n / 10)] + ' ' + (n % 10 !== 0 ? ones[n % 10] + ' ' : '');
    return ones[Math.floor(n / 100)] + ' hundred ' + convertLessThanThousand(n % 100);
  };

  const convertToWords = (n) => {
    if (n === 0) return 'zero';
    
    const units = ['', 'thousand', 'million', 'billion', 'trillion', 'quadrillion'];
    let result = '';
    let unitIndex = 0;
    let tempNum = n;
    
    while (tempNum > 0) {
      const chunk = tempNum % 1000;
      if (chunk !== 0) {
        result = convertLessThanThousand(chunk) + units[unitIndex] + ' ' + result;
      }
      tempNum = Math.floor(tempNum / 1000);
      unitIndex++;
    }
    
    return result.trim();
  };
  
  let words = convertToWords(integerPart);
  
  // ✅ FIX: Handle decimal part with leading zeros preserved
  if (decimalPartStr !== null && decimalPartStr !== '') {
    const decimalValue = parseInt(decimalPartStr, 10);
    
    // Check if decimal part has meaningful value OR has leading zeros
    if (decimalValue !== 0 || decimalPartStr.length > 1) {
      const decimalDigits = decimalPartStr.split('');
      const decimalWordsArray = decimalDigits.map(digit => {
        const digitNum = parseInt(digit, 10);
        if (digitNum === 0) return 'zero';
        if (digitNum < 20) return ones[digitNum];
        return '';
      });
      words += ` point ${decimalWordsArray.join(' ')}`;
    }
  }
  
  return words.charAt(0).toUpperCase() + words.slice(1);
};

  // ==================== GET CHILDREN BY PARENT ====================
  const getChildrenByParent = (fields) => {
    const childrenByParent = {};
    fields.forEach(field => {
      if (field.parentId) {
        if (!childrenByParent[field.parentId]) {
          childrenByParent[field.parentId] = [];
        }
        childrenByParent[field.parentId].push(field);
      }
    });
    return childrenByParent;
  };

  // ==================== COMPARE DATES ====================
  const compareDates = (date1, date2, operator) => {
    if (!date1 || !date2) return false;
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return false;
    d1.setHours(0, 0, 0, 0);
    d2.setHours(0, 0, 0, 0);

    switch (operator) {
      case "equals": return d1.getTime() === d2.getTime();
      case "not_equals": return d1.getTime() !== d2.getTime();
      case "before": return d1.getTime() < d2.getTime();
      case "after": return d1.getTime() > d2.getTime();
      case "on_or_before": return d1.getTime() <= d2.getTime();
      case "on_or_after": return d1.getTime() >= d2.getTime();
      default: return false;
    }
  };

  const isDateInRange = (date, startDate, endDate) => {
    if (!date) return false;
    const d = new Date(date);
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;
    d.setHours(0, 0, 0, 0);
    if (start) start.setHours(0, 0, 0, 0);
    if (end) end.setHours(0, 0, 0, 0);
    let valid = true;
    if (start && d < start) valid = false;
    if (end && d > end) valid = false;
    return valid;
  };

  // ==================== NOTIFICATION FUNCTIONS ====================

  // Function to send notification to specific ULB
  const sendUlbNotification = async (form, ulbId, ulbName) => {
    try {
        const notificationData = {
            title_en: `📋 New Form Assigned: ${form.form_name}`,
            title_mr: `📋 नवीन फॉर्म नियुक्त: ${form.form_name}`,
            message_en: `Form "${form.form_name}" has been assigned to your ULB (${ulbName}) for review and action.`,
            message_mr: `"${form.form_name}" फॉर्म आपल्या ULB (${ulbName}) ला पुनरावलोकन आणि कार्यवाहीसाठी नियुक्त करण्यात आला आहे.`,
            category: 'form_assignment',
            priority: 'high',
            recipient_type: 'ulb_specific',
            target_role: 'ulb',
            target_location_id: String(ulbId),
            target_location_type: 'ulb',
            created_by: currentUser?.username || 'System',
            created_by_name: currentUser?.name || currentUser?.username || 'System User',
            created_by_id: currentUser?.id || 'system',
            form_id: form.id,
            form_name: form.form_name,
            is_system_notification: 1
        };
        
        if (window.webSocket && window.webSocket.readyState === WebSocket.OPEN) {
            window.webSocket.send(JSON.stringify({
                type: 'create_notification',
                notification: notificationData
            }));
            console.log(`✅ WebSocket: Notification sent to ULB ${ulbName} (ID: ${ulbId})`);
        } else {
            const token = sessionStorage.getItem('token');
            await axios.post(`${API_BASE}/notifications/create`, notificationData, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            console.log(`✅ API: Notification sent to ULB ${ulbName} (ID: ${ulbId})`);
        }
        
        return { success: true };
        
    } catch (error) {
        console.error('❌ Error sending ULB notification:', error);
        return { success: false, error };
    }
  };

  // ==================== UPDATED: Auto notify assigned ULBs on form accept (FIRE AND FORGET) ====================
  const notifyAssignedUlbsOnAccept = async (form) => {
    try {
      let accessControlData = form.access_control || {};
      if (typeof accessControlData === 'string') {
        accessControlData = JSON.parse(accessControlData);
      }
      
      let ulbIds = [];
      
      // Extract ULB IDs from access control
      for (const [roleId, perm] of Object.entries(accessControlData)) {
        if (perm.selectedIds && perm.selectedIds.length > 0) {
          if (roleId === '1' || roleId === 'ulb' || perm.role_name?.toLowerCase().includes('ulb')) {
            ulbIds = [...ulbIds, ...perm.selectedIds];
          }
        }
      }
      
      ulbIds = [...new Set(ulbIds)];
      
      if (ulbIds.length === 0) {
        console.log('No ULBs assigned to this form');
        return;
      }
      
      console.log(`📨 Sending notifications to ${ulbIds.length} ULBs for accepted form: ${form.form_name}`);
      
      // ✅ FIRE AND FORGET - एकामागून एक न थांबता सगळे एकदम सोडा
      for (const ulbId of ulbIds) {
        const ulbName = ulbMap[ulbId] || `ULB ${ulbId}`;
        // await काढून टाका - कोणाची वाट पहात नाही
        sendUlbNotification(form, ulbId, ulbName).catch(e => 
          console.error(`Failed to send to ${ulbName}:`, e)
        );
        // delay पण काढून टाका
      }
      
    } catch (error) {
      console.error('❌ Error in notifyAssignedUlbsOnAccept:', error);
    }
  };

  const getNextAssignee = (form) => {
    try {
      const workflow = form.workflow || {};
      const currentApprovalStatus = form.approval_status;
      const sequence = workflow.sequence || [];

      if (sequence.length > 0) {
        let currentIndex = -1;
        if (currentApprovalStatus === 'pending') {
          currentIndex = 0;
        } else if (currentApprovalStatus === 'in_review') {
          const currentStep = form.current_approval_step || 0;
          currentIndex = currentStep;
        }

        if (currentIndex + 1 < sequence.length) {
          const nextRole = sequence[currentIndex + 1];
          return { type: 'role', value: nextRole };
        }
      }

      const accessControl = form.access_control || {};

      let assignedDesks = [];
      if (accessControl.selectedDesks) {
        assignedDesks = accessControl.selectedDesks;
      } else {
        for (let key in accessControl) {
          if (accessControl[key]?.selectedDesks) {
            assignedDesks = accessControl[key].selectedDesks;
            break;
          }
        }
      }

      if (assignedDesks.length > 0) {
        return { type: 'desk', value: assignedDesks[0] };
      }

      let assignedUlbs = [];
      if (accessControl['1']?.selectedIds) {
        assignedUlbs = accessControl['1'].selectedIds;
      }

      if (assignedUlbs.length > 0) {
        const ulbName = ulbMap[assignedUlbs[0]] || `ULB ${assignedUlbs[0]}`;
        return { type: 'ulb', value: assignedUlbs[0], name: ulbName };
      }

      return null;
    } catch (error) {
      console.error('Error getting next assignee:', error);
      return null;
    }
  };

  const sendNotification = (notificationData) => {
    if (window.webSocket && window.webSocket.readyState === WebSocket.OPEN) {
      window.webSocket.send(JSON.stringify({
        type: 'create_notification',
        notification: notificationData
      }));
      console.log('📨 WebSocket notification sent:', notificationData);
      if (notificationData.recipient_type !== 'ulb') {
        message.info({
          content: notificationData.title_en,
          duration: 3,
          icon: <BellOutlined />
        });
      }
    } else {
      saveNotificationToAPI(notificationData);
      console.log('⚠️ WebSocket not available, using API fallback');
    }
  };

  const saveNotificationToAPI = async (notificationData) => {
    try {
      const token = sessionStorage.getItem('token');
      await axios.post(`${API_BASE}/notifications/create`, notificationData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log('📨 Notification saved via API fallback');
    } catch (error) {
      console.error('Failed to save notification:', error);
    }
  };

 const sendAcceptNotification = (form) => {
    const creatorId = form.created_by;
    const creatorName = form.created_by_name;
    
    console.log('📨 Sending accept notification to creator:', { creatorId, creatorName, formName: form.form_name });
    
    let titleEn = `✅ Form Accepted: ${form.form_name}`;
    let titleMr = `✅ फॉर्म स्वीकारला: ${form.form_name}`;
    let messageEn = `Your form "${form.form_name}" has been accepted by ${currentUser?.name || currentUser?.username || 'Reviewer'}.`;
    let messageMr = `आपला "${form.form_name}" फॉर्म ${currentUser?.name || currentUser?.username || 'पुनरावलोकनकर्ता'} यांनी स्वीकारला आहे.`;

    if (creatorId) {
        const creatorNotification = {
            title_en: titleEn,
            title_mr: titleMr,
            message_en: messageEn,
            message_mr: messageMr,
            category: 'form_acceptance',
            priority: 'high',
            recipient_type: 'single',
            recipient_user_id: String(creatorId),
            recipient_name: creatorName,
            target_role: 'all',
            created_by: currentUser?.username || 'System',
            created_by_name: currentUser?.name || currentUser?.username || 'System User',
            created_by_id: currentUser?.id || 'system',
            form_id: form.id,
            form_name: form.form_name,
            is_system_notification: 1
        };
        
        sendNotification(creatorNotification);
        console.log(`✅ Creator notification sent to user ID: ${creatorId} (${creatorName})`);
    } else {
        console.warn('⚠️ No creator found for this form');
    }
};

  const sendRejectNotification = (form, reason) => {
    const notificationData = {
      title_en: `❌ Form Rejected: ${form.form_name}`,
      title_mr: `❌ फॉर्म नाकारला: ${form.form_name}`,
      message_en: `Form "${form.form_name}" has been rejected by ${currentUser?.name || currentUser?.username || 'Reviewer'}.\nReason: ${reason}`,
      message_mr: `"${form.form_name}" फॉर्म ${currentUser?.name || currentUser?.username || 'पुनरावलोकनकर्ता'} यांनी नाकारला आहे.\nकारण: ${reason}`,
      category: 'system',
      priority: 'high',
      recipient_type: 'all',
      target_role: 'all',
      created_by: currentUser?.username || 'System',
      created_by_name: currentUser?.name || currentUser?.username || 'System User',
      created_by_id: currentUser?.id || 'system'
    };

    sendNotification(notificationData);
  };

const renderEditableField = (field, allFields, childrenByParent, level = 0) => {
  const children = childrenByParent[field.id] || [];
  const value = editableValues[field.id];
  const currentLanguage = language || 'english';
  const unitInfo = getUnitDisplayInfo(field);
  
  // Build field map for external references
  const buildFieldMap = (fields) => {
    const fieldMap = {};
    const buildMap = (fieldList) => {
      fieldList.forEach(f => {
        fieldMap[f.id] = f;
        if (f.children) buildMap(f.children);
        if (f.headingChildren) buildMap(f.headingChildren);
        if (f.subFields) buildMap(f.subFields);
        if (f.arithmeticFields) buildMap(f.arithmeticFields);
        if (f.formulaSteps) {
          f.formulaSteps.forEach(step => {
            if (step.arithmeticFields) buildMap(step.arithmeticFields);
          });
        }
      });
    };
    buildMap(fields);
    return fieldMap;
  };
  
  const fieldMap = buildFieldMap(allFields);
  
  const activeRules = field.type === 'text' ? buildTextActiveRules(field) :
                      field.type === 'number' ? buildNumberActiveRules(field, unitInfo) : [];

  // ==================== FORMULA FIELD WITH EXTERNAL REFERENCE SUPPORT ====================
  if (field.type === "formula") {
    const formulaSteps = field.formulaSteps || [];
    const stepCombineOp = field.stepCombineOperator || 'add';
    const showResult = field.showResult !== false;
    const finalResultLabel = field.finalResultLabel || { en: 'Final Result', mr: 'अंतिम निकाल' };
    const finalFalseMessage = field.finalFalseMessage || '';
    const isLogicalCombine = ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_equal', 'less_than_equal'].includes(stepCombineOp);
    const isSingleStep = formulaSteps.length === 1;
    const isNoneCombine = stepCombineOp === 'none';

    const getFormulaUnitDisplay = (fieldData) => {
      if (!fieldData) return null;
      if (fieldData.selectedUnitObject) return fieldData.selectedUnitObject.unit_symbol;
      if (fieldData.units?.enabled && fieldData.units.selectedUnitSymbol) return fieldData.units.selectedUnitSymbol;
      return null;
    };

    const getFormulaConstantValue = (fieldData) => {
      if (fieldData.constantValue !== undefined && fieldData.constantValue !== null) {
        return fieldData.constantValue;
      }
      return 0;
    };

    // Calculate arithmetic result with external field support
    const calculateArithmeticLocal = (values, arithmeticFields, operator) => {
      if (!arithmeticFields || arithmeticFields.length === 0) return 0;
      const numbers = arithmeticFields.map(f => {
        let val = parseFloat(values[f.id]) || 0;
        const unitInfo = getUnitDisplayInfo(f);
        if (unitInfo && unitInfo.value && unitInfo.value !== 1) {
          val = val * parseFloat(unitInfo.value);
        }
        return val;
      });
      switch (operator) {
        case "add": return numbers.reduce((a, b) => a + b, 0);
        case "subtract": return numbers.reduce((a, b) => a - b);
        case "multiply": return numbers.reduce((a, b) => a * b, 1);
        case "divide": return numbers.reduce((a, b) => a / b);
        case "average": return numbers.reduce((a, b) => a + b, 0) / numbers.length;
        case "percentage": return numbers.length >= 2 ? (numbers[0] / 100) * numbers[1] : 0;
        default: return numbers.reduce((a, b) => a + b, 0);
      }
    };

    // Calculate formula step result with external references
const calculateFormulaStepResult = (step, values, visitedFields = new Set()) => {
  const fields = step.arithmeticFields || [];
  if (fields.length === 0) return 0;

  let result = 0;
  let isFirst = true;
  const operator = step.combineOperator || 'add';

  for (const fieldData of fields) {
    let val;

    // ===== FIX: Handle Step Result Reference FIRST =====
    if (fieldData.isStepResultReference === true) {
      // sourceStepId वरून step result काढा
      const sourceStepId = fieldData.sourceStepId;
      if (sourceStepId) {
        // Step result values मध्ये store केले आहे का?
        if (values && values[`step_${sourceStepId}_result`] !== undefined) {
          val = values[`step_${sourceStepId}_result`];
        } else if (values && values[sourceStepId] !== undefined) {
          val = values[sourceStepId];
        } else {
          val = 0;
        }
      } else {
        val = 0;
      }
    }
    // Handle external formula field references
    else if (fieldData.isSharedReference === true || fieldData.isExternalReference === true) {
      const sourceId = fieldData.sourceFieldId || fieldData.id;
      
      if (visitedFields.has(sourceId)) {
        val = 0;
      } else {
        visitedFields.add(sourceId);
        
        if (fieldData.sourceFieldType === 'formula') {
          const originalFormulaField = fieldMap[sourceId];
          if (originalFormulaField && originalFormulaField.type === 'formula') {
            val = calculateFormulaFinalResult(originalFormulaField, values, visitedFields);
          } else {
            val = values[sourceId] !== undefined ? parseFloat(values[sourceId]) || 0 : 0;
          }
        } 
        else if (fieldData.sourceFieldType === 'arithmetic') {
          const originalArithmeticField = fieldMap[sourceId];
          if (originalArithmeticField && originalArithmeticField.type === 'arithmetic') {
            val = calculateArithmeticLocal(values, originalArithmeticField.arithmeticFields, originalArithmeticField.arithmeticOperator);
          } else {
            val = values[sourceId] !== undefined ? parseFloat(values[sourceId]) || 0 : 0;
          }
        }
        else {
          val = values[sourceId] !== undefined ? parseFloat(values[sourceId]) || 0 : 0;
        }
        
        visitedFields.delete(sourceId);
      }
    }
    else if (fieldData.fieldType === 'constant') {
      val = getFormulaConstantValue(fieldData);
    }
    else {
      val = values[fieldData.id] !== undefined ? parseFloat(values[fieldData.id]) || 0 : 0;
    }

    // Apply unit conversion
    const unitInfo = getUnitDisplayInfo(fieldData);
    if (unitInfo && unitInfo.value && unitInfo.value !== 1) {
      val = val * parseFloat(unitInfo.value);
    }

    const validation = fieldData.validation || {};
    if (validation.allowNegative === false && val < 0) val = 0;
    if (validation.allowZero === false && val === 0) val = 0.01;
    if (validation.integerOnly) val = Math.floor(val);
    if (validation.decimalPlaces !== null && validation.decimalPlaces !== undefined && validation.allowDecimals !== false) {
      val = parseFloat(val.toFixed(validation.decimalPlaces));
    }

    if (isFirst) {
      result = val;
      isFirst = false;
    } else {
      switch (operator) {
        case 'add': result += val; break;
        case 'subtract': result -= val; break;
        case 'multiply': result *= val; break;
        case 'divide': result = val !== 0 ? result / val : 0; break;
        case 'percentage': result = val !== 0 ? (result / val) * 100 : 0; break;
        case 'equals': result = (result === val) ? 1 : 0; break;
        case 'not_equals': result = (result !== val) ? 1 : 0; break;
        case 'greater_than': result = (result > val) ? 1 : 0; break;
        case 'less_than': result = (result < val) ? 1 : 0; break;
        case 'greater_than_equal': result = (result >= val) ? 1 : 0; break;
        case 'less_than_equal': result = (result <= val) ? 1 : 0; break;
        default: result += val;
      }
    }
  }
  return result;
};

    const calculateFormulaFinalResult = (formulaField, values, visitedFields = new Set()) => {
      if (visitedFields.has(formulaField.id)) return 0;
      visitedFields.add(formulaField.id);
      
      const steps = formulaField.formulaSteps || [];
      const combineOp = formulaField.stepCombineOperator || 'add';
      const isNone = combineOp === 'none';
      
      if (steps.length === 0) {
        visitedFields.delete(formulaField.id);
        return 0;
      }

      const stepResults = [];
      for (const step of steps) {
        stepResults.push(calculateFormulaStepResult(step, values, visitedFields));
      }

      let finalResult;
      if (steps.length === 1 || isNone) {
        finalResult = stepResults[0] || 0;
      } else {
        finalResult = stepResults[0] || 0;
        for (let i = 1; i < stepResults.length; i++) {
          switch (combineOp) {
            case 'add': finalResult += stepResults[i]; break;
            case 'subtract': finalResult -= stepResults[i]; break;
            case 'multiply': finalResult *= stepResults[i]; break;
            case 'divide': finalResult = stepResults[i] !== 0 ? finalResult / stepResults[i] : 0; break;
            case 'percentage': finalResult = stepResults[i] !== 0 ? (finalResult / stepResults[i]) * 100 : 0; break;
            case 'equals': finalResult = finalResult === stepResults[i] ? 1 : 0; break;
            case 'not_equals': finalResult = finalResult !== stepResults[i] ? 1 : 0; break;
            case 'greater_than': finalResult = finalResult > stepResults[i] ? 1 : 0; break;
            case 'less_than': finalResult = finalResult < stepResults[i] ? 1 : 0; break;
            case 'greater_than_equal': finalResult = finalResult >= stepResults[i] ? 1 : 0; break;
            case 'less_than_equal': finalResult = finalResult <= stepResults[i] ? 1 : 0; break;
            default: finalResult += stepResults[i];
          }
        }
      }
      
      visitedFields.delete(formulaField.id);
      return finalResult;
    };

    const finalResult = calculateFormulaFinalResult(field, editableValues);
    const finalUnitInfo = getFormulaUnitDisplay(field);
    const finalResultWords = !isLogicalCombine && finalResult !== 0 ? getWordsRepresentation(finalResult) : '';
    const finalDisplayValue = !isLogicalCombine ? (finalResult || 0).toFixed(2) : finalResult;

    // Calculate step results
// Calculate step results
const stepResultsMap = {};
const allValues = { ...editableValues };
formulaSteps.forEach(step => {
  const stepResult = calculateFormulaStepResult(step, allValues);
  stepResultsMap[step.id] = stepResult;
  // IMPORTANT: Store step result in values for other steps to use
  allValues[step.id] = stepResult;
  allValues[`step_${step.id}_result`] = stepResult;
});

    return (
      <div key={field.id} style={{ marginBottom: 24, marginLeft: field.parentId ? 32 : 0 }}>
        {formulaSteps.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 20, background: '#fafafa', borderRadius: 8 }}>
            <Text type="secondary">No formula steps configured</Text>
          </div>
        ) : (
          <>
            {formulaSteps.map((step, stepIdx) => {
              const visibleFields = (step.arithmeticFields || []).filter(f => 
                !f.isSharedReference || f.isExternalReference
              );
              const stepResult = stepResultsMap[step.id] || 0;
              const stepIsLogical = isLogicalOperator(step.combineOperator);
              const showStepResult = step.showStepResult !== false;
              const stepFalseMessage = step.falseMessage || '';
              const stepResultLabel = currentLanguage === 'english' 
                ? (step.resultLabel?.en || `${step.name} Result`)
                : (step.resultLabel?.mr || `${step.name} चे निकाल`);
              
              const stepResultWords = !stepIsLogical && stepResult !== 0 ? getWordsRepresentation(stepResult) : '';

              return (
                <div key={step.id} style={{ marginBottom: 24 }}>
                  {showStepResult && (
                    <>
                      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <Badge count={stepIdx + 1} style={{ backgroundColor: '#1890ff' }} />
                        <Text strong style={{ fontSize: '14px' }}>{step.name}</Text>
                        {stepIsLogical && stepFalseMessage && (
                          <Tag color="red" style={{ fontSize: '10px' }}>
                            <MessageOutlined /> Custom False Msg
                          </Tag>
                        )}
                      </div>

                      {visibleFields.length > 0 && (
                        <div style={{ marginBottom: 16, paddingLeft: 16 }}>
  {visibleFields.map((f, fidx) => {
    const unitSymbol = getFormulaUnitDisplay(f);
    const isConstant = f.fieldType === 'constant';
    const constantValue = isConstant ? getFormulaConstantValue(f) : null;
    let currentValue = 0;
    let isReadOnly = false;
    
    // ===== FIX 1: Handle Step Result Reference FIRST =====
    if (f.isStepResultReference === true) {
      isReadOnly = true;
      const sourceStepId = f.sourceStepId;
      if (sourceStepId) {
        // stepResultsMap मधून value घ्या
        const stepResultValue = stepResultsMap[sourceStepId];
        if (stepResultValue !== undefined && stepResultValue !== null) {
          currentValue = stepResultValue;
        } else {
          currentValue = 0;
        }
      } else {
        currentValue = 0;
      }
    }
    // ===== Handle Shared/External Reference =====
    else if (f.isSharedReference === true || f.isExternalReference === true) {
      const sourceId = f.sourceFieldId || f.id;
      const sourceField = fieldMap[sourceId];
      isReadOnly = true;
      
      if (f.sourceFieldType === 'formula' && sourceField && sourceField.type === 'formula') {
        currentValue = calculateFormulaFinalResult(sourceField, editableValues);
      } 
      else if (f.sourceFieldType === 'arithmetic' && sourceField && sourceField.type === 'arithmetic') {
        currentValue = calculateArithmeticLocal(editableValues, sourceField.arithmeticFields, sourceField.arithmeticOperator);
      }
      else {
        currentValue = editableValues[sourceId] !== undefined ? editableValues[sourceId] : 0;
      }
    } 
    else if (isConstant) {
      currentValue = constantValue;
      isReadOnly = true;
    } 
    else {
      currentValue = editableValues[f.id] !== undefined ? editableValues[f.id] : 0;
      isReadOnly = false;
    }
    
    const wordsRepresentation = !isConstant && currentValue !== 0 && !isNaN(parseFloat(currentValue)) 
      ? getWordsRepresentation(currentValue) : '';
    
    const displayValue = typeof currentValue === 'number' ? currentValue.toFixed(2) : currentValue;

    return (
      <div key={f.id} style={{ marginBottom: 12 }}>
        <div style={{ marginBottom: 4, fontWeight: 600, fontSize: '13px', color: '#333' }}>
          {currentLanguage === 'english' ? (f.enLabel || 'Value') : (f.mrLabel || 'मूल्य')}
          {f.enRequired && !isConstant && !f.isSharedReference && <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>}
          {isConstant && <Tag color="gold" style={{ marginLeft: 8, fontSize: '9px' }}>Constant</Tag>}
          
          {/* ===== FIX 2: Add Step Result Tag ===== */}
          {f.isStepResultReference && (
            <Tag color="magenta" style={{ marginLeft: 8, fontSize: '9px' }}>
              <ShareAltOutlined /> Step Result
            </Tag>
          )}
          
          {f.isExternalReference && f.sourceFieldType === 'formula' && (
            <Tag color="purple" style={{ marginLeft: 8, fontSize: '9px' }}>
              <FunctionOutlined /> Formula Result
            </Tag>
          )}
          {f.isExternalReference && f.sourceFieldType === 'arithmetic' && (
            <Tag color="orange" style={{ marginLeft: 8, fontSize: '9px' }}>
              <CalculatorOutlined /> Arithmetic Result
            </Tag>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* ===== FIX 3: Add Step Result Reference to display condition ===== */}
          {(isConstant || f.isSharedReference || f.isExternalReference || f.isStepResultReference) ? (
            <div style={{ 
              width: 180, 
              padding: '6px 11px', 
              background: f.isStepResultReference ? '#f9f0ff' : (f.isExternalReference ? '#f0fdf4' : '#f5f5f5'), 
              border: f.isStepResultReference ? '1px solid #d3adf7' : (f.isExternalReference ? '1px solid #b7eb8f' : '1px solid #d9d9d9'), 
              borderRadius: '6px',
              minHeight: '32px',
              display: 'flex',
              alignItems: 'center'
            }}>
              <Text strong style={{ color: f.isStepResultReference ? '#722ed1' : (f.isExternalReference ? '#389e0d' : '#333') }}>
                {displayValue}
              </Text>
              {f.isStepResultReference && (
                <Tooltip title={`From step: ${f.sourceStepName || f.sourceStepId}`}>
                  <InfoCircleOutlined style={{ marginLeft: 8, color: '#722ed1', fontSize: 12 }} />
                </Tooltip>
              )}
              {f.isExternalReference && (
                <Tooltip title="Auto-calculated from source formula">
                  <InfoCircleOutlined style={{ marginLeft: 8, color: '#52c41a', fontSize: 12 }} />
                </Tooltip>
              )}
            </div>
          ) : (
            <div>
              <Input
                type="text"
                inputMode={f.validation?.integerOnly ? "numeric" : "decimal"}
                placeholder={currentLanguage === 'english' ? (f.enPlaceholder || 'Enter value') : (f.mrPlaceholder || 'मूल्य प्रविष्ट करा')}
                value={currentValue}
                onChange={(e) => {
                  let newValue = e.target.value;
                  
                  // 🔥 For integer-only fields, strip decimal points immediately
                  if (f.validation?.integerOnly) {
                    newValue = newValue.replace(/\./g, '');
                    if (f.validation?.allowNegative) {
                      newValue = newValue.replace(/[^-0-9]/g, '');
                      if (newValue.indexOf('-') > 0) newValue = newValue.replace(/-/g, '');
                      if (newValue === '-') newValue = '';
                    } else {
                      newValue = newValue.replace(/[^0-9]/g, '');
                    }
                    // For integer fields, parse as integer
                    const parsedValue = newValue === '' ? '' : parseInt(newValue, 10) || 0;
                    setEditableValues(prev => ({ ...prev, [f.id]: parsedValue }));
                  }
                  // 🔥 For decimal fields, apply validation and keep the value as string for proper decimal handling
                  else {
                    // Apply exact length validation if configured
                    if (f.validation?.exactLength) {
                      const exactLen = f.validation.exactLength;
                      const digitsOnly = newValue.replace(/[^0-9]/g, '');
                      if (digitsOnly.length > exactLen) {
                        const allowedDigits = digitsOnly.slice(0, exactLen);
                        const hasNegative = newValue.startsWith('-');
                        const decimalIndex = newValue.indexOf('.');
                        if (decimalIndex !== -1 && f.validation?.allowDecimals !== false) {
                          newValue = (hasNegative ? '-' : '') + allowedDigits + '.' + newValue.slice(decimalIndex + 1);
                        } else {
                          newValue = (hasNegative ? '-' : '') + allowedDigits;
                        }
                      }
                    }
                    
                    // 🔥 Apply number validation to handle decimals properly
                    const { transformed, error } = applyNumberValidation(f, newValue);
                    setEditableValues(prev => ({ ...prev, [f.id]: transformed }));
                    if (error && transformed) message.error(error);
                  }
                }}
                style={{ width: 180 }}
                size="small"
              />
              {/* Show validation rules for the field */}
              {f.validation?.decimalPlaces > 0 && f.validation?.allowDecimals !== false && !f.validation?.integerOnly && (
                <div style={{ marginTop: 4 }}>
                  <Tag color="cyan" style={{ fontSize: '10px' }}>
                    🔢 {f.validation.decimalPlaces} decimal place(s)
                  </Tag>
                </div>
              )}
              {f.validation?.integerOnly && (
                <div style={{ marginTop: 4 }}>
                  <Tag color="purple" style={{ fontSize: '10px' }}>
                    🔢 Integer only (no decimals)
                  </Tag>
                </div>
              )}
              {f.validation?.allowNegative === false && !f.validation?.integerOnly && (
                <div style={{ marginTop: 4 }}>
                  <Tag color="orange" style={{ fontSize: '10px' }}>
                    ➕ Positive only
                  </Tag>
                </div>
              )}
            </div>
          )}
          {unitSymbol && (
            <div style={{ 
              padding: '0 12px', 
              background: '#f5f5f5', 
              borderRadius: '4px', 
              fontSize: '14px', 
              color: '#666', 
              height: '32px', 
              display: 'flex', 
              alignItems: 'center', 
              border: '1px solid #d9d9d9' 
            }}>
              {unitSymbol}
            </div>
          )}
        </div>
        {wordsRepresentation && !isNaN(parseFloat(currentValue)) && parseFloat(currentValue) !== 0 && (
          <div style={{ marginTop: 6, padding: '4px 8px', background: '#f6ffed', borderRadius: '4px', display: 'inline-block' }}>
            <Text style={{ fontSize: '10px', color: '#333' }}>
              {wordsRepresentation}
              {unitSymbol && <span style={{ marginLeft: 4, color: '#666' }}>({unitSymbol})</span>}
            </Text>
          </div>
        )}
      </div>
    );
  })}
</div>
                      )}

                      <div style={{ marginTop: 8, paddingLeft: 16 }}>
                        {stepIsLogical ? (
                          stepResult === 0 && stepFalseMessage ? (
                            <div style={{ padding: '8px 12px', background: '#ffebee', border: '1px solid #ffcdd2', borderRadius: '6px', color: '#c62828', display: 'inline-block' }}>
                              <CloseCircleOutlined style={{ marginRight: 8 }} />
                              <Text style={{ color: '#c62828' }}>{stepFalseMessage}</Text>
                            </div>
                          ) : null
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                            <Text style={{ fontSize: '13px', color: '#666' }}>{stepResultLabel}:</Text>
                            <Tag color="green" style={{ fontSize: '13px', padding: '4px 12px' }}>
                              {typeof stepResult === 'number' ? stepResult.toFixed(2) : stepResult}
                            </Tag>
                            {stepResultWords && stepResult !== 0 && (
                              <Text type="secondary" style={{ fontSize: '11px' }}>({stepResultWords})</Text>
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}

            {!isSingleStep && !isNoneCombine && showResult && finalResult !== null && (
              <div style={{ 
                marginTop: 16, 
                paddingTop: 16, 
                borderTop: '1px solid #e8e8e8',
                padding: '12px 20px',
                background: isLogicalCombine && finalResult === 0 ? '#fff2f0' : '#f0f5ff',
                borderRadius: 8,
                border: isLogicalCombine && finalResult === 0 ? '1px solid #ffccc7' : '1px solid #adc6ff'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
                  <Text strong style={{ fontSize: '14px' }}>
                    {currentLanguage === 'english' ? finalResultLabel.en : finalResultLabel.mr}:
                    {finalUnitInfo?.symbol && (
                      <Tag color="blue" style={{ marginLeft: 8, fontSize: 12 }}>{finalUnitInfo.symbol}</Tag>
                    )}
                  </Text>
                  <div>
                    {isLogicalCombine ? (
                      finalResult === 1 ? (
                        <span style={{ color: '#999' }}>—</span>
                      ) : (
                        <Tag color="error" style={{ fontSize: '14px', padding: '4px 16px', margin: 0 }}>
                          <CloseCircleOutlined /> {finalFalseMessage || (currentLanguage === 'english' ? 'FALSE' : 'फॉल्स')}
                        </Tag>
                      )
                    ) : (
                      <Tag color="green" style={{ fontSize: '16px', padding: '6px 20px', fontWeight: 'bold' }}>
                        {finalDisplayValue}
                      </Tag>
                    )}
                  </div>
                  {!isLogicalCombine && finalResultWords && finalResult !== 0 && (
                    <Text type="secondary" style={{ fontSize: '12px' }}>({finalResultWords})</Text>
                  )}
                </div>
              </div>
            )}

            {(currentLanguage === 'english' ? field.enHelpText : field.mrHelpText) && (
              <div style={{ marginTop: 12, textAlign: 'center' }}>
                <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
                  {currentLanguage === 'english' ? field.enHelpText : field.mrHelpText}
                </Text>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // ==================== HEADING FIELD ====================
  if (field.type === "heading") {
    const headingLevel = field.headingLevel || 1;
    const marginLeft = headingLevel === 1 ? 0 : headingLevel === 2 ? 24 : headingLevel === 3 ? 48 : 64;

    return (
      <div key={field.id} style={{ marginBottom: 16, marginLeft }}>
        {headingLevel === 1 && (
          <div style={{
            fontSize: '24px',
            fontWeight: 600,
            color: '#1f1f1f',
            borderBottom: '3px solid #fa8c16',
            paddingBottom: '8px',
            marginBottom: '8px'
          }}>
            {currentLanguage === 'english' ? (field.enLabel || field.label) : (field.mrLabel || field.label)}
          </div>
        )}

        {headingLevel === 2 && (
          <div style={{
            fontSize: '20px',
            fontWeight: 500,
            color: '#2c3e50',
            borderBottom: '2px solid #ffd591',
            paddingBottom: '4px',
            marginBottom: '8px'
          }}>
            {currentLanguage === 'english' ? (field.enLabel || field.label) : (field.mrLabel || field.label)}
          </div>
        )}

        {headingLevel >= 3 && (
          <div style={{
            fontSize: '18px',
            fontWeight: 500,
            color: '#34495e',
            borderBottom: '1px dashed #ffe7ba',
            paddingBottom: '3px',
            marginBottom: '8px'
          }}>
            {currentLanguage === 'english' ? (field.enLabel || field.label) : (field.mrLabel || field.label)}
          </div>
        )}

        {(field.enHelpText || field.mrHelpText) && (
          <div style={{ marginTop: '4px', marginBottom: '12px', fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
            {currentLanguage === 'english' ? field.enHelpText : field.mrHelpText}
          </div>
        )}

        {children.length > 0 && (
          <div style={{ marginLeft: headingLevel === 1 ? 24 : 16, borderLeft: headingLevel >= 2 ? '2px solid #f0f0f0' : 'none', paddingLeft: 16 }}>
            {children.map(child => renderEditableField(child, allFields, childrenByParent, level + 1))}
          </div>
        )}
      </div>
    );
  }

  // ==================== PARAGRAPH FIELD ====================
  if (field.type === "paragraph") {
    return (
      <div key={field.id} style={{ marginBottom: 16, marginLeft: field.parentId ? 64 : 0 }}>
        <Text style={{ fontSize: "14px", lineHeight: "1.6", color: "#495057", display: "block" }}>
          {currentLanguage === 'english' ? (field.enLabel || field.label) : (field.mrLabel || field.label)}
        </Text>
        {(field.enHelpText || field.mrHelpText) && (
          <Text type="secondary" style={{ fontSize: "12px", display: "block", marginTop: 4 }}>
            {currentLanguage === 'english' ? field.enHelpText : field.mrHelpText}
          </Text>
        )}
      </div>
    );
  }

  // ==================== RADIO FIELD ====================
  if (field.type === "radio") {
    const options = field.enOptions || field.options || [];
    const mrOptions = field.mrOptions || [];

    return (
      <div key={field.id} style={{ marginBottom: 20, marginLeft: field.parentId ? 64 : 0 }}>
        <div style={{ marginBottom: 8 }}>
          <Text strong>
            {currentLanguage === 'english' ? (field.enLabel || field.label) : (field.mrLabel || field.label)}
            {field.enRequired && <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>}
          </Text>
        </div>

        <Radio.Group
          value={value}
          onChange={(e) => {
            setEditableValues({
              ...editableValues,
              [field.id]: e.target.value
            });
          }}
        >
          {options.map((opt, idx) => (
            <Radio key={idx} value={opt}>
              {currentLanguage === 'english' ? opt : (mrOptions[idx] || opt)}
            </Radio>
          ))}
        </Radio.Group>

        {(field.enHelpText || field.mrHelpText) && (
          <Text type="secondary" style={{ fontSize: "12px", display: "block", marginTop: 4 }}>
            {currentLanguage === 'english' ? field.enHelpText : field.mrHelpText}
          </Text>
        )}

        {value && (
          <div style={{
            marginTop: 16,
            padding: "16px",
            background: "#f8f9fa",
            borderRadius: "8px",
            border: "1px solid #e9ecef"
          }}>
            <Text type="secondary" style={{ marginBottom: 12, display: "block" }}>
              {currentLanguage === 'english' ? 'Fields for' : 'साठी फील्ड'} "{value}":
            </Text>
            {children.filter(f => f.colName === value).map(subField =>
              renderEditableField(subField, allFields, childrenByParent, level + 1)
            )}
          </div>
        )}
      </div>
    );
  }

  // ==================== CHECKBOX FIELD ====================
  if (field.type === "checkbox") {
    const options = field.enOptions || field.options || [];
    const mrOptions = field.mrOptions || [];
    const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);

    return (
      <div key={field.id} style={{ marginBottom: 20, marginLeft: field.parentId ? 64 : 0 }}>
        <div style={{ marginBottom: 8 }}>
          <Text strong>
            {currentLanguage === 'english' ? (field.enLabel || field.label) : (field.mrLabel || field.label)}
            {field.enRequired && <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>}
          </Text>
        </div>

        <Checkbox.Group
          value={selectedValues}
          onChange={(values) => {
            setEditableValues({
              ...editableValues,
              [field.id]: values
            });
          }}
        >
          {options.map((opt, idx) => (
            <Checkbox key={idx} value={opt}>
              {currentLanguage === 'english' ? opt : (mrOptions[idx] || opt)}
            </Checkbox>
          ))}
        </Checkbox.Group>

        {(field.enHelpText || field.mrHelpText) && (
          <Text type="secondary" style={{ fontSize: "12px", display: "block", marginTop: 4 }}>
            {currentLanguage === 'english' ? field.enHelpText : field.mrHelpText}
          </Text>
        )}

        {selectedValues.length > 0 && (
          <div style={{
            marginTop: 16,
            padding: "16px",
            background: "#f6ffed",
            borderRadius: "8px",
            border: "1px solid #b7eb8f"
          }}>
            <Text type="secondary" style={{ marginBottom: 12, display: "block" }}>
              {currentLanguage === 'english' ? 'Fields for selected options' : 'निवडलेल्या पर्यायांसाठी फील्ड'}:
            </Text>
            {selectedValues.map(selectedOption => (
              <div key={selectedOption} style={{ marginBottom: 12 }}>
                <div style={{
                  padding: "8px 12px",
                  background: "#e6f7ff",
                  borderLeft: "3px solid #1890ff",
                  marginBottom: 8
                }}>
                  <Checkbox checked disabled style={{ marginRight: 8 }} />
                  <Text strong>{selectedOption}</Text>
                </div>
                {children.filter(f => f.colName === selectedOption).map(subField =>
                  renderEditableField(subField, allFields, childrenByParent, level + 1)
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ==================== DROPDOWN FIELD ====================
  if (field.type === "dropdown") {
    const options = field.enOptions || field.options || [];
    const mrOptions = field.mrOptions || [];

    const selectOptions = options.map((opt, idx) => ({
      label: currentLanguage === 'english' ? opt : (mrOptions[idx] || opt),
      value: opt
    }));

    return (
      <div key={field.id} style={{ marginBottom: 20, marginLeft: field.parentId ? 64 : 0 }}>
        <div style={{ marginBottom: 8 }}>
          <Text strong>
            {currentLanguage === 'english' ? (field.enLabel || field.label) : (field.mrLabel || field.label)}
            {field.enRequired && <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>}
          </Text>
          {field.allowSearch && (
            <Tag color="cyan" style={{ marginLeft: 8, fontSize: '10px' }}>
              <SearchOutlined /> Searchable
            </Tag>
          )}
          {field.allowMultiSelect && (
            <Tag color="green" style={{ marginLeft: 4, fontSize: '10px' }}>
              <CheckSquareOutlined /> Multi-select
            </Tag>
          )}
        </div>

        <Select
          value={value}
          onChange={(val) => {
            setEditableValues({
              ...editableValues,
              [field.id]: val
            });
          }}
          style={{ width: "100%", maxWidth: 400 }}
          placeholder={currentLanguage === 'english' ? (field.enPlaceholder || "Select option...") : (field.mrPlaceholder || "पर्याय निवडा...")}
          options={selectOptions}
          allowClear={!field.enRequired}
          showSearch={field.allowSearch}
          mode={field.allowMultiSelect ? "multiple" : undefined}
        />

        {(field.enHelpText || field.mrHelpText) && (
          <Text type="secondary" style={{ fontSize: "12px", display: "block", marginTop: 4 }}>
            {currentLanguage === 'english' ? field.enHelpText : field.mrHelpText}
          </Text>
        )}
      </div>
    );
  }

  // ==================== DATE FIELD ====================
  if (field.type === "date") {
    const dateValidation = applyDateValidation(field, value);
    const dateRules = buildDateRules(field);

    let comparisonValidation = { valid: true, message: '' };

    if (field.enableDateComparison && value) {
      const selectedDate = value;
      if (field.comparisonOperator === 'current_date') {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (selectedDate !== today.toISOString().split('T')[0]) {
          comparisonValidation = {
            valid: false,
            message: field.customValidationMessage || `Date must be today's date (${today.toISOString().split('T')[0]})`
          };
        }
      } else if (field.comparisonType === 'single' && field.compareWithDate) {
        const isValid = compareDates(selectedDate, field.compareWithDate, field.comparisonOperator);
        if (!isValid) {
          comparisonValidation = {
            valid: false,
            message: field.customValidationMessage || `Date must be ${field.comparisonOperator} ${field.compareWithDate}`
          };
        }
      } else if (field.comparisonType === 'range' && field.startDate && field.endDate) {
        const isValid = isDateInRange(selectedDate, field.startDate, field.endDate);
        if (!isValid) {
          comparisonValidation = {
            valid: false,
            message: field.customValidationMessage || `Date must be between ${field.startDate} and ${field.endDate}`
          };
        }
      }
    }

    const finalValid = dateValidation.valid && comparisonValidation.valid;
    const finalError = dateValidation.error || comparisonValidation.message;

    return (
      <div key={field.id} style={{ marginBottom: 20, marginLeft: field.parentId ? 64 : 0 }}>
        <div style={{ marginBottom: 8 }}>
          <Text strong>
            {currentLanguage === 'english' ? (field.enLabel || field.label) : (field.mrLabel || field.label)}
            {field.enRequired && <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>}
          </Text>
          {field.enableDateComparison && (
            <Tag color="orange" style={{ marginLeft: 8 }}>
              <SwapOutlined /> {field.comparisonOperator === 'current_date' ? 'Current Date ON' : 'Comparison ON'}
            </Tag>
          )}
          {dateRules.length > 0 && (
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {dateRules.map((rule, idx) => (
                <Tag key={idx} color={rule.color} style={{ fontSize: '10px', margin: 0 }}>{rule.text}</Tag>
              ))}
            </div>
          )}
        </div>

        <DatePicker
          value={value ? moment(value) : null}
          onChange={(date, dateString) => {
            setEditableValues({
              ...editableValues,
              [field.id]: dateString
            });
          }}
          style={{ width: "100%", maxWidth: 300, borderColor: finalValid ? undefined : "#ff4d4f" }}
          format="YYYY-MM-DD"
          placeholder="Select date"
          status={finalValid ? undefined : "error"}
        />

        {!finalValid && (
          <Text type="danger" style={{ fontSize: "12px", display: "block", marginTop: 4 }}>
            {finalError}
          </Text>
        )}

        {(field.enHelpText || field.mrHelpText) && finalValid && (
          <Text type="secondary" style={{ fontSize: "12px", display: "block", marginTop: 4 }}>
            {currentLanguage === 'english' ? field.enHelpText : field.mrHelpText}
          </Text>
        )}
      </div>
    );
  }

  // ==================== NUMBER FIELD WITH WORD DISPLAY ====================
  if (field.type === "number") {
    const currentValue = editableValues[field.id] || '';
    const validation = applyNumberValidation(field, currentValue);
    const exactLength = field.validation?.exactLength;

    if (validation.transformed !== currentValue && validation.transformed !== undefined) {
      if (editableValues[field.id] !== validation.transformed) {
        setTimeout(() => setEditableValues(prev => ({ ...prev, [field.id]: validation.transformed })), 0);
      }
    }

    const displayValue = validation.transformed !== undefined ? validation.transformed : currentValue;
    const numericValue = parseFloat(displayValue);
    const hasValue = displayValue !== '' && !isNaN(numericValue);
    const textRepresentation = hasValue ? numberToWords(displayValue) : '';

    return (
      <div key={field.id} style={{ marginBottom: 20, marginLeft: field.parentId ? 64 : 0 }}>
        <div style={{ marginBottom: 8 }}>
          <Text strong>
            {currentLanguage === 'english' ? (field.enLabel || field.label) : (field.mrLabel || field.label)}
            {field.enRequired && <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>}
          </Text>
          {activeRules.length > 0 && (
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {activeRules.map((rule, idx) => (
                <Tag key={idx} color={rule.color} style={{ fontSize: '10px', margin: 0, borderRadius: '12px' }}>
                  {rule.text}
                </Tag>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <Input
            type="text"
            inputMode="numeric"
            maxLength={exactLength ? exactLength + (displayValue.includes('.') ? 1 : 0) + (displayValue.startsWith('-') ? 1 : 0) : undefined}
            value={validation.transformed}
            onChange={(e) => {
              let newValue = e.target.value;
              if (exactLength) {
                const digitsOnly = newValue.replace(/[^0-9]/g, '');
                if (digitsOnly.length > exactLength) {
                  const allowedDigits = digitsOnly.slice(0, exactLength);
                  const hasNegative = newValue.startsWith('-');
                  const decimalIndex = newValue.indexOf('.');
                  if (decimalIndex !== -1) {
                    newValue = (hasNegative ? '-' : '') + allowedDigits + '.' + newValue.slice(decimalIndex + 1);
                  } else {
                    newValue = (hasNegative ? '-' : '') + allowedDigits;
                  }
                }
              }
              const { transformed, error } = applyNumberValidation(field, newValue);
              setEditableValues({
                ...editableValues,
                [field.id]: transformed
              });
              if (error && transformed) message.error(error);
            }}
            style={{
              width: "100%",
              maxWidth: 300,
              borderColor: validation.error ? "#ff4d4f" : undefined,
              fontFamily: "monospace"
            }}
            placeholder={currentLanguage === 'english' ? (field.enPlaceholder || "Enter number...") : (field.mrPlaceholder || "संख्या प्रविष्ट करा...")}
            status={validation.error ? "error" : undefined}
          />
          {unitInfo?.symbol && (
            <div style={{
              padding: '0 12px',
              background: '#f5f5f5',
              borderRadius: '4px',
              fontSize: '14px',
              color: '#666',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              border: '1px solid #d9d9d9'
            }}>
              {unitInfo.symbol}
            </div>
          )}
        </div>

        {hasValue && !validation.error && textRepresentation && (
          <div style={{
            marginTop: 12,
            padding: '10px 16px',
            background: '#f6ffed',
            borderRadius: '8px',
            border: '1px solid #b7eb8f',
            display: 'inline-block',
            maxWidth: '100%'
          }}>
            <Space align="start" size="small" wrap>
              <Text style={{ fontSize: '14px', color: '#333' }}>
                {textRepresentation.charAt(0).toUpperCase() + textRepresentation.slice(1)}
                {unitInfo?.name && (
                  <span style={{ marginLeft: 4, color: '#666' }}>
                    ({unitInfo.name})
                  </span>
                )}
              </Text>
            </Space>
          </div>
        )}

        {validation.error && (
          <Text type="danger" style={{ fontSize: "12px", display: "block", marginTop: 4 }}>
            {validation.error}
          </Text>
        )}

        {!validation.error && displayValue !== '' && (
          <div style={{ marginTop: 8 }}>
            <Space size={4} wrap>
              <Tag color="green" icon={<CheckCircleOutlined />} style={{ fontSize: '10px' }}>
                Valid number
              </Tag>
              {field.validation?.allowDecimals === false && !displayValue?.includes('.') && (
                <Tag color="orange" style={{ fontSize: '10px' }}>
                  <NumberOutlined /> Integer
                </Tag>
              )}
              {parseFloat(displayValue) > 0 && field.validation?.allowNegative === false && (
                <Tag color="geekblue" style={{ fontSize: '10px' }}>
                  <NumberOutlined /> Positive
                </Tag>
              )}
            </Space>
          </div>
        )}

        {(field.enHelpText || field.mrHelpText) && !validation.error && (
          <Text type="secondary" style={{ fontSize: "12px", display: "block", marginTop: 4 }}>
            {currentLanguage === 'english' ? field.enHelpText : field.mrHelpText}
          </Text>
        )}
      </div>
    );
  }

  // ==================== TEXT FIELD WITH VALIDATION ====================
  if (field.type === "text") {
    const currentValue = editableValues[field.id] || '';
    const validation = applyTextValidation(field, currentValue);
    const exactLength = field.validation?.exactLength;

    if (validation.transformed !== currentValue && validation.transformed !== undefined) {
      if (editableValues[field.id] !== validation.transformed) {
        setTimeout(() => setEditableValues(prev => ({ ...prev, [field.id]: validation.transformed })), 0);
      }
    }

    return (
      <div key={field.id} style={{ marginBottom: 20, marginLeft: field.parentId ? 64 : 0 }}>
        <div style={{ marginBottom: 8 }}>
          <Text strong>
            {currentLanguage === 'english' ? (field.enLabel || field.label) : (field.mrLabel || field.label)}
            {field.enRequired && <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>}
          </Text>
          {activeRules.length > 0 && (
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {activeRules.map((rule, idx) => (
                <Tag key={idx} color={rule.color} style={{ fontSize: '10px', margin: 0 }}>
                  {rule.text}
                </Tag>
              ))}
            </div>
          )}
        </div>

        <Input
          maxLength={exactLength || undefined}
          value={validation.transformed}
          onChange={(e) => {
            let newValue = e.target.value;
            if (exactLength && newValue.length > exactLength) {
              newValue = newValue.slice(0, exactLength);
            }
            const { transformed, error } = applyTextValidation(field, newValue);
            setEditableValues({
              ...editableValues,
              [field.id]: transformed
            });
            if (error && transformed) message.error(error);
          }}
          style={{
            width: "100%",
            borderColor: validation.error ? "#ff4d4f" : undefined,
            textTransform: field.validation?.uppercase ? "uppercase" : field.validation?.lowercase ? "lowercase" : "none",
            fontFamily: field.validation?.languageScript === "marathi_only" ? "'Noto Sans Devanagari', 'Nirmala UI', sans-serif" : "inherit"
          }}
          placeholder={currentLanguage === 'english' ? (field.enPlaceholder || "Enter text...") : (field.mrPlaceholder || "मजकूर प्रविष्ट करा...")}
          status={validation.error ? "error" : undefined}
        />

        {validation.error && (
          <Text type="danger" style={{ fontSize: "12px", display: "block", marginTop: 4 }}>
            {validation.error}
          </Text>
        )}

        {!validation.error && validation.transformed && (
          <div style={{ marginTop: 4 }}>
            <Tag color="green" icon={<CheckCircleOutlined />} style={{ fontSize: '10px' }}>
              Valid input
            </Tag>
          </div>
        )}

        {(field.enHelpText || field.mrHelpText) && !validation.error && (
          <Text type="secondary" style={{ fontSize: "12px", display: "block", marginTop: 4 }}>
            {currentLanguage === 'english' ? field.enHelpText : field.mrHelpText}
          </Text>
        )}
      </div>
    );
  }

  // ==================== FILE FIELD ====================
  if (field.type === "file") {
    const fileList = editableFileList[field.id] || [];

    return (
      <div key={field.id} style={{ marginBottom: 20, marginLeft: field.parentId ? 64 : 0 }}>
        <div style={{ marginBottom: 8 }}>
          <Text strong>
            {currentLanguage === 'english' ? (field.enLabel || field.label) : (field.mrLabel || field.label)}
            {field.enRequired && <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>}
          </Text>
          <div style={{ marginTop: 4 }}>
            <Tag color="blue" style={{ fontSize: '10px' }}>
              <UploadOutlined /> Max: {field.maxFileSize || 5}MB
            </Tag>
            {field.maxFileCount > 1 && (
              <Tag color="green" style={{ fontSize: '10px', marginLeft: 4 }}>
                Up to {field.maxFileCount} files
              </Tag>
            )}
          </div>
        </div>

        <Upload
          fileList={fileList}
          onChange={(info) => {
            setEditableFileList({
              ...editableFileList,
              [field.id]: info.fileList
            });
          }}
          multiple={field.maxFileCount > 1}
          accept={field.acceptedFileTypes || ".jpg,.jpeg,.png,.gif,.bmp,.pdf,.doc,.docx,.xls,.xlsx,.txt"}
          beforeUpload={() => false}
        >
          <Button icon={<UploadOutlined />}>
            {currentLanguage === 'english' ? 'Upload File' : 'फाइल अपलोड करा'}
          </Button>
        </Upload>

        {(field.enHelpText || field.mrHelpText) && (
          <Text type="secondary" style={{ fontSize: "12px", display: "block", marginTop: 4 }}>
            {currentLanguage === 'english' ? field.enHelpText : field.mrHelpText}
          </Text>
        )}
      </div>
    );
  }

  // ==================== COMPARISON FIELD ====================
  if (field.type === "comparison") {
    const checkComparisonValues = (mainVal, calcVal, operator) => {
      const main = parseFloat(mainVal) || 0;
      const calculated = parseFloat(calcVal) || 0;
      
      switch (operator) {
        case "equals": return main === calculated;
        case "not_equals": return main !== calculated;
        case "greater_than": return main > calculated;
        case "less_than": return main < calculated;
        case "greater_than_equal": return main >= calculated;
        case "less_than_equal": return main <= calculated;
        default: return main === calculated;
      }
    };

    const getOperatorSymbol = (operator) => {
      switch (operator) {
        case "equals": return "=";
        case "not_equals": return "≠";
        case "greater_than": return ">";
        case "less_than": return "<";
        case "greater_than_equal": return "≥";
        case "less_than_equal": return "≤";
        default: return "=";
      }
    };

    const getOperatorText = (operator, lang) => {
      if (lang === 'marathi') {
        switch (operator) {
          case "equals": return "समान";
          case "not_equals": return "समान नाही";
          case "greater_than": return "पेक्षा मोठे";
          case "less_than": return "पेक्षा लहान";
          case "greater_than_equal": return "पेक्षा मोठे किंवा समान";
          case "less_than_equal": return "पेक्षा लहान किंवा समान";
          default: return "समान";
        }
      }
      switch (operator) {
        case "equals": return "equal to";
        case "not_equals": return "not equal to";
        case "greater_than": return "greater than";
        case "less_than": return "less than";
        case "greater_than_equal": return "greater than or equal to";
        case "less_than_equal": return "less than or equal to";
        default: return "equal to";
      }
    };

    const mainValue = editableValues[field.mainField?.id] || '';
    const mainUnitInfo = getUnitDisplayInfo(field.mainField);
    const mainValidation = applyNumberValidation(field.mainField, mainValue);
    const mainActiveRules = buildNumberActiveRules(field.mainField, mainUnitInfo);
    const mainTextRepresentation = mainValue !== '' && !mainValidation.error ? numberToWords(mainValue) : '';

    if (mainValidation.transformed !== mainValue && mainValidation.transformed !== undefined) {
      if (editableValues[field.mainField?.id] !== mainValidation.transformed) {
        setTimeout(() => setEditableValues(prev => ({ ...prev, [field.mainField.id]: mainValidation.transformed })), 0);
      }
    }

    let calculatedValue = 0;
    if (field.subFields && field.subFields.length > 0) {
      const numbers = [];
      field.subFields.forEach(subField => {
        let subValue = parseFloat(editableValues[subField.id]) || 0;
        const subUnitInfo = getUnitDisplayInfo(subField);
        if (subUnitInfo && subUnitInfo.value && subUnitInfo.value !== 1) {
          subValue = subValue * parseFloat(subUnitInfo.value);
        }
        numbers.push(subValue);
      });

      let tempResult = numbers[0] || 0;
      for (let i = 1; i < numbers.length; i++) {
        const operator = field.subFields[i]?.arithmeticOperator || "add";
        if (operator === "add") tempResult += numbers[i];
        else if (operator === "subtract") tempResult -= numbers[i];
        else if (operator === "multiply") tempResult *= numbers[i];
        else if (operator === "divide") tempResult = numbers[i] !== 0 ? tempResult / numbers[i] : 0;
        else if (operator === "percentage") tempResult = (numbers[0] / 100) * numbers[i];
      }
      calculatedValue = tempResult;
    }

    const isValid = mainValue !== '' ? checkComparisonValues(mainValue, calculatedValue, field.comparisonOperator) : null;
    const calculatedTextRepresentation = calculatedValue !== 0 ? numberToWords(calculatedValue) : '';

    return (
      <Card key={field.id} size="small" style={{ marginBottom: 16, marginLeft: field.parentId ? 64 : 0, background: "#f8f9fa", border: "1px solid #fa8c16", borderRadius: "8px" }}>
        <div style={{ marginBottom: 12 }}>
          <Text strong>
            {currentLanguage === 'english' ? (field.enLabel || field.label) : (field.mrLabel || field.label)}
          </Text>
          <Tag color="orange" style={{ marginLeft: 8 }}>
            <SwapOutlined /> {getOperatorSymbol(field.comparisonOperator)}
          </Tag>
        </div>

        <Row align="middle" gutter={16} style={{ marginBottom: 12 }}>
          <Col span={8} style={{ textAlign: "right", paddingRight: 16 }}>
            <div>
              <Text>{currentLanguage === 'english' ? field.mainField?.enLabel : field.mainField?.mrLabel}</Text>
              {mainActiveRules.length > 0 && (
                <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {mainActiveRules.map((rule, ridx) => (
                    <Tag key={ridx} color={rule.color} style={{ fontSize: '9px', margin: 0 }}>{rule.text}</Tag>
                  ))}
                </div>
              )}
            </div>
          </Col>
          <Col span={16}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
             <Input
  type="text"
  inputMode={field.mainField?.validation?.integerOnly ? "numeric" : "decimal"}
  placeholder={currentLanguage === 'english' ? field.mainField?.enPlaceholder : field.mainField?.mrPlaceholder}
  onChange={(e) => {
    let newValue = e.target.value;
    
    if (field.mainField?.validation?.integerOnly) {
      newValue = newValue.replace(/\./g, '');
      if (field.mainField?.validation?.allowNegative) {
        newValue = newValue.replace(/[^-0-9]/g, '');
        if (newValue.indexOf('-') > 0) newValue = newValue.replace(/-/g, '');
        if (newValue === '-') newValue = '';
      } else {
        newValue = newValue.replace(/[^0-9]/g, '');
      }
    }
    
    const { transformed, error } = applyNumberValidation(field.mainField, newValue);
    setEditableValues(prev => ({ ...prev, [field.mainField.id]: transformed }));
    if (error && transformed && !field.mainField?.validation?.integerOnly) message.error(error);
  }}
  value={mainValidation.transformed}
  style={{ borderRadius: "6px", flex: 1, borderColor: mainValidation.error && !field.mainField?.validation?.integerOnly ? "#ff4d4f" : undefined }}
  status={mainValidation.error && !field.mainField?.validation?.integerOnly ? "error" : undefined}
/>
              {mainUnitInfo?.symbol && (
                <div style={{ padding: '0 12px', background: '#f5f5f5', borderRadius: '4px', height: '32px', display: 'flex', alignItems: 'center', border: '1px solid #d9d9d9' }}>
                  {mainUnitInfo.symbol}
                </div>
              )}
            </div>
            {mainValidation.error && <Text type="danger" style={{ fontSize: "11px", display: "block", marginTop: 4 }}>{mainValidation.error}</Text>}
            {mainValue !== '' && mainTextRepresentation && !mainValidation.error && (
              <div style={{ marginTop: 8, padding: '6px 12px', background: '#f6ffed', borderRadius: '6px', border: '1px solid #b7eb8f' }}>
                <Text style={{ fontSize: '12px', color: '#333' }}>
                  {mainTextRepresentation.charAt(0).toUpperCase() + mainTextRepresentation.slice(1)}
                  {mainUnitInfo?.name && <span style={{ marginLeft: 4, color: '#666' }}>({mainUnitInfo.name})</span>}
                </Text>
              </div>
            )}
          </Col>
        </Row>

        {field.subFields?.map((subField, idx) => {
          const subUnitInfo = getUnitDisplayInfo(subField);
          const currentSubValue = editableValues[subField.id] || '';
          const subValidation = applyNumberValidation(subField, currentSubValue);
          const subActiveRules = buildNumberActiveRules(subField, subUnitInfo);
          const subTextRepresentation = currentSubValue !== '' && !subValidation.error ? numberToWords(currentSubValue) : '';

          if (subValidation.transformed !== currentSubValue && subValidation.transformed !== undefined) {
            if (editableValues[subField.id] !== subValidation.transformed) {
              setTimeout(() => setEditableValues(prev => ({ ...prev, [subField.id]: subValidation.transformed })), 0);
            }
          }

          return (
            <Row align="middle" gutter={16} key={subField.id} style={{ marginBottom: 12 }}>
              <Col span={8} style={{ textAlign: "right", paddingRight: 16 }}>
                <div>
                  {idx > 0 && (
                    <Tag color="default" style={{ marginRight: 4 }}>
                      {subField.arithmeticOperator === "add" ? "+" :
                       subField.arithmeticOperator === "subtract" ? "-" :
                       subField.arithmeticOperator === "multiply" ? "×" :
                       subField.arithmeticOperator === "divide" ? "÷" : "%"}
                    </Tag>
                  )}
                  <Text>{currentLanguage === 'english' ? (subField.enLabel || subField.label) : (subField.mrLabel || subField.label)}</Text>
                  {subActiveRules.length > 0 && (
                    <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {subActiveRules.map((rule, ridx) => (
                        <Tag key={ridx} color={rule.color} style={{ fontSize: '9px', margin: 0 }}>{rule.text}</Tag>
                      ))}
                    </div>
                  )}
                </div>
              </Col>
              <Col span={16}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
<Input
  type="text"
  inputMode={subField.validation?.integerOnly ? "numeric" : "decimal"}
  placeholder={currentLanguage === 'english' ? (subField.enPlaceholder || subField.placeholder) : (subField.mrPlaceholder || subField.placeholder)}
  onChange={(e) => {
    let newValue = e.target.value;
    
    if (subField.validation?.integerOnly) {
      newValue = newValue.replace(/\./g, '');
      if (subField.validation?.allowNegative) {
        newValue = newValue.replace(/[^-0-9]/g, '');
        if (newValue.indexOf('-') > 0) newValue = newValue.replace(/-/g, '');
        if (newValue === '-') newValue = '';
      } else {
        newValue = newValue.replace(/[^0-9]/g, '');
      }
    }
    
    const { transformed, error } = applyNumberValidation(subField, newValue);
    setEditableValues(prev => ({ ...prev, [subField.id]: transformed }));
    if (error && transformed && !subField.validation?.integerOnly) message.error(error);
  }}
  value={subValidation.transformed}
  style={{ borderRadius: "6px", flex: 1, borderColor: subValidation.error && !subField.validation?.integerOnly ? "#ff4d4f" : undefined }}
  status={subValidation.error && !subField.validation?.integerOnly ? "error" : undefined}
/>
                  {subUnitInfo?.symbol && (
                    <div style={{ padding: '0 12px', background: '#f5f5f5', borderRadius: '4px', height: '32px', display: 'flex', alignItems: 'center', border: '1px solid #d9d9d9' }}>
                      {subUnitInfo.symbol}
                    </div>
                  )}
                </div>
                {subValidation.error && <Text type="danger" style={{ fontSize: "11px", display: "block", marginTop: 4 }}>{subValidation.error}</Text>}
                {currentSubValue !== '' && subTextRepresentation && !subValidation.error && (
                  <div style={{ marginTop: 6, padding: '4px 10px', background: '#f6ffed', borderRadius: '4px', border: '1px solid #b7eb8f' }}>
                    <Text style={{ fontSize: '11px', color: '#333' }}>
                      📝 {subTextRepresentation.charAt(0).toUpperCase() + subTextRepresentation.slice(1)}
                      {subUnitInfo?.name && <span style={{ marginLeft: 4, color: '#666' }}>({subUnitInfo.name})</span>}
                    </Text>
                  </div>
                )}
              </Col>
            </Row>
          );
        })}

        {field.subFields?.length > 0 && (
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={8} style={{ textAlign: "right", paddingRight: 16 }}>
              <Text type="secondary" style={{ fontWeight: 500 }}>Calculated Result:</Text>
            </Col>
            <Col span={16}>
              <div style={{ padding: "12px 16px", background: "#ffffff", borderRadius: "6px", border: "1px solid #e9ecef" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                  <Text strong style={{ fontSize: "16px" }}>= {calculatedValue.toFixed(2)}</Text>
                  {mainValue !== '' && (
                    <Tag color={isValid ? "green" : "red"}>
                      {isValid ? "✓ Valid Match" : `✗ Does NOT Match (${mainValue} ${getOperatorSymbol(field.comparisonOperator)} ${calculatedValue.toFixed(2)})`}
                    </Tag>
                  )}
                </div>
                {calculatedTextRepresentation && calculatedValue !== 0 && (
                  <div style={{ marginTop: 8, padding: '6px 10px', background: '#e6f7ff', borderRadius: '4px' }}>
                    <Text style={{ fontSize: '12px', color: '#333' }}>
                      📝 {calculatedTextRepresentation.charAt(0).toUpperCase() + calculatedTextRepresentation.slice(1)}
                    </Text>
                  </div>
                )}
                {mainValue !== '' && isValid === false && (
                  <div style={{ marginTop: "12px", padding: "10px 12px", background: "#ffebee", border: "1px solid #ffcdd2", borderRadius: "8px" }}>
                    <Text strong style={{ color: "#c62828" }}>
                      {field.customValidationMessage || (
                        currentLanguage === 'english' 
                          ? `✗ Validation Failed: ${mainValue} must be ${getOperatorText(field.comparisonOperator, 'english')} ${calculatedValue.toFixed(2)}`
                          : `✗ मान्यता अयशस्वी: ${mainValue} ${getOperatorText(field.comparisonOperator, 'marathi')} ${calculatedValue.toFixed(2)} असणे आवश्यक आहे`
                      )}
                    </Text>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        )}
      </Card>
    );
  }

  // ==================== ARITHMETIC FIELD ====================
  if (field.type === "arithmetic") {
    let calculatedResult = 0;
    let validationErrors = [];
    let allFieldsValid = true;
    const validatedNumbers = [];
    
    if (field.arithmeticFields && field.arithmeticFields.length > 0) {
      for (let idx = 0; idx < field.arithmeticFields.length; idx++) {
        const afield = field.arithmeticFields[idx];
        const currentValue = editableValues[afield.id] || '';
        const unitInfo = getUnitDisplayInfo(afield);
        const validation = applyNumberValidation(afield, currentValue);
        
        if (validation.error) {
          validationErrors.push({
            field: afield,
            index: idx,
            error: validation.error,
            value: currentValue
          });
          allFieldsValid = false;
          validatedNumbers.push({ value: 0, valid: false, error: validation.error });
        } else {
          let numericValue = parseFloat(validation.transformed) || 0;
          if (unitInfo && unitInfo.value && unitInfo.value !== 1) {
            numericValue = numericValue * parseFloat(unitInfo.value);
          }
          validatedNumbers.push({ 
            value: numericValue, 
            valid: true, 
            error: null,
            originalValue: validation.transformed,
            unitInfo: unitInfo
          });
        }
      }
    }
    
    if (allFieldsValid && validatedNumbers.length > 0) {
      const numbers = validatedNumbers.map(v => v.value);
      switch (field.arithmeticOperator) {
        case "add": calculatedResult = numbers.reduce((a, b) => a + b, 0); break;
        case "subtract": calculatedResult = numbers.reduce((a, b) => a - b); break;
        case "multiply": calculatedResult = numbers.reduce((a, b) => a * b, 1); break;
        case "divide": 
          if (numbers.slice(1).some(n => n === 0)) {
            validationErrors.push({ error: "Division by zero is not allowed" });
            allFieldsValid = false;
          } else {
            calculatedResult = numbers.reduce((a, b) => a / b);
          }
          break;
        case "average": calculatedResult = numbers.reduce((a, b) => a + b, 0) / numbers.length; break;
        case "percentage":
          if (numbers.length >= 2) {
            if (numbers[1] === 0) {
              validationErrors.push({ error: "Base value cannot be zero for percentage calculation" });
              allFieldsValid = false;
            } else {
              calculatedResult = (numbers[0] / numbers[1]) * 100;
            }
          }
          break;
        default: calculatedResult = numbers.reduce((a, b) => a + b, 0);
      }
    }

    const editedResult = editableValues[`${field.id}_result`];
    const displayResult = editedResult !== undefined ? editedResult : calculatedResult;
    const resultValidation = field.resultField ? applyNumberValidation(field.resultField, displayResult.toString()) : { transformed: displayResult, error: null };
    const hasResultError = resultValidation.error && !allFieldsValid;
    const resultTextRepresentation = (displayResult !== 0 && allFieldsValid && !hasResultError) ? numberToWords(displayResult) : '';

    const getOperationSymbol = () => {
      switch (field.arithmeticOperator) {
        case "add": return "+";
        case "subtract": return "-";
        case "multiply": return "×";
        case "divide": return "÷";
        case "average": return "avg";
        case "percentage": return "%";
        default: return "+";
      }
    };

    const getOperationLabel = () => {
      switch (field.arithmeticOperator) {
        case "add": return currentLanguage === 'english' ? 'Addition' : 'बेरीज';
        case "subtract": return currentLanguage === 'english' ? 'Subtraction' : 'वजाबाकी';
        case "multiply": return currentLanguage === 'english' ? 'Multiplication' : 'गुणाकार';
        case "divide": return currentLanguage === 'english' ? 'Division' : 'भागाकार';
        case "average": return currentLanguage === 'english' ? 'Average' : 'सरासरी';
        case "percentage": return currentLanguage === 'english' ? 'Percentage' : 'टक्केवारी';
        default: return currentLanguage === 'english' ? 'Calculation' : 'गणना';
      }
    };

    return (
      <Card key={field.id} size="small" style={{ 
        marginBottom: 16, 
        marginLeft: field.parentId ? 64 : 0, 
        background: allFieldsValid ? "#f8f9fa" : "#fff2f0",
        border: allFieldsValid ? "1px solid #52c41a" : "1px solid #ffccc7",
        borderRadius: "8px" 
      }}>
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div>
            <Text strong>
              {currentLanguage === 'english' ? (field.enLabel || field.label) : (field.mrLabel || field.label)}
              {field.enRequired && <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>}
            </Text>
            <Tag color="green" style={{ marginLeft: 8 }}>
              <CalculatorOutlined /> {getOperationLabel()} ({getOperationSymbol()})
            </Tag>
            {!allFieldsValid && (
              <Tag color="red" style={{ marginLeft: 8 }}>
                <CloseCircleOutlined /> Validation Failed
              </Tag>
            )}
          </div>
        </div>

        {field.arithmeticFields?.map((afield, idx) => {
          const currentAfieldValue = editableValues[afield.id] || '';
          const afieldUnitInfo = getUnitDisplayInfo(afield);
          const afieldValidation = applyNumberValidation(afield, currentAfieldValue);
          const afieldActiveRules = buildNumberActiveRules(afield, afieldUnitInfo);
          const hasError = !!afieldValidation.error;
          const afieldTextRepresentation = (currentAfieldValue !== '' && !hasError) ? numberToWords(currentAfieldValue) : '';

          if (afieldValidation.transformed !== currentAfieldValue && afieldValidation.transformed !== undefined) {
            if (editableValues[afield.id] !== afieldValidation.transformed) {
              setTimeout(() => setEditableValues(prev => ({ ...prev, [afield.id]: afieldValidation.transformed })), 0);
            }
          }

          const isPercentageFirst = field.arithmeticOperator === "percentage" && idx === 0;
          const isPercentageSecond = field.arithmeticOperator === "percentage" && idx === 1;

          return (
            <Row align="middle" gutter={16} key={afield.id} style={{ marginBottom: 12 }}>
              <Col span={8} style={{ textAlign: "right", paddingRight: 16 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px', flexWrap: 'wrap' }}>
                    <Text style={{ color: hasError ? '#ff4d4f' : 'inherit' }}>
                      {currentLanguage === 'english' ? (afield.enLabel || afield.label) : (afield.mrLabel || afield.label)}
                      {isPercentageFirst && <Tag color="orange" style={{ marginLeft: 4, fontSize: '9px' }}>% Value</Tag>}
                      {isPercentageSecond && <Tag color="blue" style={{ marginLeft: 4, fontSize: '9px' }}>Base Value</Tag>}
                      {afield.enRequired && <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>}
                    </Text>
                    {hasError && (
                      <Tooltip title={afieldValidation.error}>
                        <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '12px' }} />
                      </Tooltip>
                    )}
                    {!hasError && currentAfieldValue !== '' && (
                      <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '12px' }} />
                    )}
                  </div>
                  {afieldActiveRules.length > 0 && (
                    <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'flex-end' }}>
                      {afieldActiveRules.map((rule, ridx) => (
                        <Tag key={ridx} color={rule.color} style={{ fontSize: '9px', margin: 0 }}>{rule.text}</Tag>
                      ))}
                    </div>
                  )}
                </div>
              </Col>
              <Col span={16}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
<Input
  type="text"
  inputMode={afield.validation?.integerOnly ? "numeric" : "decimal"}
  placeholder={currentLanguage === 'english' ? (afield.enPlaceholder || afield.placeholder) : (afield.mrPlaceholder || afield.placeholder)}
  onChange={(e) => {
    let newValue = e.target.value;
    
    // 🔥 Apply exact length validation at input level (same as number field)
    if (afield.validation?.exactLength && !afield.validation?.integerOnly) {
      const exactLen = afield.validation.exactLength;
      const digitsOnly = newValue.replace(/[^0-9]/g, '');
      if (digitsOnly.length > exactLen) {
        const allowedDigits = digitsOnly.slice(0, exactLen);
        const hasNegative = newValue.startsWith('-');
        const decimalIndex = newValue.indexOf('.');
        if (decimalIndex !== -1 && afield.validation?.allowDecimals !== false) {
          newValue = (hasNegative ? '-' : '') + allowedDigits + '.' + newValue.slice(decimalIndex + 1);
        } else {
          newValue = (hasNegative ? '-' : '') + allowedDigits;
        }
      }
    }
    
    // 🔥 For integer-only fields, strip decimal points
    if (afield.validation?.integerOnly) {
      newValue = newValue.replace(/\./g, '');
      if (afield.validation?.allowNegative) {
        newValue = newValue.replace(/[^-0-9]/g, '');
        if (newValue.indexOf('-') > 0) newValue = newValue.replace(/-/g, '');
        if (newValue === '-') newValue = '';
      } else {
        newValue = newValue.replace(/[^0-9]/g, '');
      }
    }
    
    const { transformed, error } = applyNumberValidation(afield, newValue);
    setEditableValues(prev => ({ ...prev, [afield.id]: transformed }));
    if (error && transformed) message.error(error);
    // Clear edited result when input changes
    const newValues = { ...editableValues, [afield.id]: transformed };
    delete newValues[`${field.id}_result`];
    setEditableValues(newValues);
  }}
  value={afieldValidation.transformed}
  style={{ 
    borderRadius: "6px", 
    flex: 1, 
    borderColor: hasError ? "#ff4d4f" : undefined,
    backgroundColor: hasError ? '#fff2f0' : 'white'
  }}
  status={hasError ? "error" : undefined}
/>
                  {afieldUnitInfo?.symbol && (
                    <div style={{ 
                      padding: '0 12px', 
                      background: '#f5f5f5', 
                      borderRadius: '4px', 
                      fontSize: '14px', 
                      color: '#666', 
                      height: '32px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      border: '1px solid #d9d9d9' 
                    }}>
                      {afieldUnitInfo.symbol}
                    </div>
                  )}
                </div>
                {hasError && (
                  <Text type="danger" style={{ fontSize: "11px", display: "block", marginTop: 4 }}>
                    {afieldValidation.error}
                  </Text>
                )}
                {!hasError && currentAfieldValue !== '' && afieldTextRepresentation && (
                  <div style={{ marginTop: 6, padding: '4px 10px', background: '#f6ffed', borderRadius: '4px', border: '1px solid #b7eb8f' }}>
                    <Text style={{ fontSize: '11px', color: '#333' }}>
                      📝 {afieldTextRepresentation.charAt(0).toUpperCase() + afieldTextRepresentation.slice(1)}
                      {afieldUnitInfo?.name && <span style={{ marginLeft: 4, color: '#666' }}>({afieldUnitInfo.name})</span>}
                    </Text>
                  </div>
                )}
              </Col>
            </Row>
          );
        })}

        {validationErrors.length > 0 && !allFieldsValid && (
          <Alert
            message={currentLanguage === 'english' ? 'Validation Errors' : 'वैधता त्रुटी'}
            description={
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {validationErrors.map((err, idx) => (
                  <li key={idx}>
                    {err.field?.enLabel || err.field?.label || `Field ${err.index + 1}`}: {err.error}
                  </li>
                ))}
              </ul>
            }
            type="error"
            showIcon
            style={{ marginBottom: 16, marginTop: 8 }}
          />
        )}

        <Row gutter={16} style={{ marginTop: 20 }}>
          <Col span={8} style={{ textAlign: "right", paddingRight: 16 }}>
            <Text type="secondary" style={{ fontSize: "14px", fontWeight: 500 }}>
              {currentLanguage === 'english' ? (field.resultField?.enLabel || "Result") : (field.resultField?.mrLabel || "परिणाम")}:
              {field.resultField?.enRequired && <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>}
            </Text>
          </Col>
          <Col span={16}>
            <div style={{
              padding: "16px 20px",
              background: hasResultError ? "#fff2f0" : (allFieldsValid ? "#f6ffed" : "#fff7e6"),
              border: hasResultError ? "2px solid #ffccc7" : (allFieldsValid ? "2px solid #b7eb8f" : "2px solid #ffe58f"),
              borderRadius: "8px"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
                <Space size={12}>
                  <CalculatorOutlined style={{ 
                    color: hasResultError ? "#ff4d4f" : (allFieldsValid ? "#52c41a" : "#faad14"), 
                    fontSize: "20px" 
                  }} />
                  <Text strong style={{ 
                    fontSize: "20px", 
                    color: hasResultError ? "#ff4d4f" : (allFieldsValid ? "#389e0d" : "#d48806") 
                  }}>
                    {allFieldsValid ? displayResult.toFixed(2) : 'Invalid'}
                    {(() => {
                      const resultUnitInfo = getUnitDisplayInfo(field.resultField);
                      if (resultUnitInfo?.symbol && allFieldsValid) {
                        return <Tag color="blue" style={{ marginLeft: 8, fontSize: '12px' }}>{resultUnitInfo.symbol}</Tag>;
                      }
                      return null;
                    })()}
                  </Text>
                </Space>
                <Button size="small" type="link" onClick={() => {
                  const newValues = { ...editableValues };
                  delete newValues[`${field.id}_result`];
                  setEditableValues(newValues);
                }}>
                  {currentLanguage === 'english' ? 'Reset to calculated' : 'गणना केलेल्यावर रीसेट करा'}
                </Button>
              </div>
              
              {allFieldsValid && resultTextRepresentation && displayResult !== 0 && !hasResultError && (
                <div style={{ marginTop: 10, padding: '8px 12px', background: '#e6f7ff', borderRadius: '6px' }}>
                  <Text style={{ fontSize: '13px', color: '#333' }}>
                    📝 {resultTextRepresentation.charAt(0).toUpperCase() + resultTextRepresentation.slice(1)}
                    {(() => {
                      const resultUnitInfo = getUnitDisplayInfo(field.resultField);
                      if (resultUnitInfo?.name) return <span style={{ marginLeft: 4, color: '#666' }}>({resultUnitInfo.name})</span>;
                      return null;
                    })()}
                  </Text>
                </div>
              )}
              
              {hasResultError && (
                <Text type="danger" style={{ fontSize: "12px", display: "block", marginTop: 8 }}>
                  {resultValidation.error}
                </Text>
              )}
              
              {!allFieldsValid && !hasResultError && (
                <Text type="warning" style={{ fontSize: "12px", display: "block", marginTop: 8 }}>
                  {currentLanguage === 'english' 
                    ? 'Please fix validation errors above to see the result'
                    : 'परिणाम पाहण्यासाठी कृपया वरील वैधता त्रुटी सुधारा'}
                </Text>
              )}
            </div>
          </Col>
        </Row>

        {field.arithmeticOperator === "percentage" && (
          <Alert
            message={currentLanguage === 'english'
              ? "Percentage calculation: (Value1% of Value2)"
              : "टक्केवारी गणना: (मूल्य१% of मूल्य२)"}
            type="info"
            showIcon
            style={{ marginTop: 12 }}
          />
        )}
      </Card>
    );
  }

  // ==================== DEFAULT FALLBACK ====================
  return (
    <div key={field.id} style={{ marginBottom: 12, marginLeft: field.parentId ? 64 : 0 }}>
      <div style={{ marginBottom: 4 }}>
        <Text strong>
          {currentLanguage === 'english' ? (field.enLabel || field.label) : (field.mrLabel || field.label)}
        </Text>
      </div>
      <Input
        value={value}
        onChange={(e) => {
          setEditableValues({
            ...editableValues,
            [field.id]: e.target.value
          });
        }}
      />
    </div>
  );
};

  // ==================== MAIN EDITABLE FORM PREVIEW ====================
  const renderEditableFormPreview = () => {
    if (!selectedForm || !selectedForm.schema) {
      return <Text type="secondary">No form schema available</Text>;
    }

    let schema = selectedForm.schema;
    if (typeof schema === 'string') {
      try {
        schema = JSON.parse(schema);
      } catch (e) {
        return <Text type="secondary">Invalid schema format</Text>;
      }
    }

    const fields = Array.isArray(schema) ? schema.filter(f => f.type !== 'system_report') : [];
    const childrenByParent = getChildrenByParent(fields);
    const rootFields = fields.filter(f => !f.parentId);

    if (rootFields.length === 0) {
      return <Text type="secondary">No fields found in this form</Text>;
    }

    return (
      <div style={{
        maxHeight: '60vh',
        overflowY: 'auto',
        paddingRight: '16px',
        paddingLeft: '8px'
      }}>
        <AntForm layout="vertical">
          {rootFields.map(field => renderEditableField(field, fields, childrenByParent))}
        </AntForm>
      </div>
    );
  };

  // ==================== GET ALL FIELDS WITH REPORT CONFIG ====================

  useEffect(() => {
    fetchAllUlbs();
  }, []);

  const fetchAllUlbs = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/meta/ulbs/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data?.success && Array.isArray(response.data?.data)) {
        const ulbsData = response.data.data;
        const map = {};
        ulbsData.forEach(ulb => {
          map[ulb.id] = ulb.ulb_name || `ULB ${ulb.id}`;
        });
        setUlbMap(map);
        setAllUlbs(ulbsData);
        console.log(`✅ Loaded ${ulbsData.length} ULBs for name mapping`);
      }
    } catch (error) {
      console.error("Failed to fetch ULBs:", error);
    }
  };

  const toggleShowAllUlbs = (roleId) => {
    setShowAllUlbs(prev => ({
      ...prev,
      [roleId]: !prev[roleId]
    }));
  };

  const getAllFieldsWithReportConfig = (fields, selectedFields = [], operations = {}) => {
    let result = [];
    for (const field of fields) {
      if (field.type === 'system_report') continue;

      result.push({
        id: field.id,
        label: field.enLabel || field.label || field.id,
        type: field.type,
        isSelected: selectedFields.includes(field.id),
        operator: operations[field.id]?.operator || null,
        isImportant: operations[field.id]?.important || false
      });

      if (field.children) {
        if (Array.isArray(field.children)) {
          result = [...result, ...getAllFieldsWithReportConfig(field.children, selectedFields, operations)];
        } else {
          if (field.children.left) result = [...result, ...getAllFieldsWithReportConfig(field.children.left, selectedFields, operations)];
          if (field.children.right) result = [...result, ...getAllFieldsWithReportConfig(field.children.right, selectedFields, operations)];
        }
      }
      if (field.headingChildren) {
        result = [...result, ...getAllFieldsWithReportConfig(field.headingChildren, selectedFields, operations)];
      }
      if (field.subFields) {
        result = [...result, ...getAllFieldsWithReportConfig(field.subFields, selectedFields, operations)];
      }
      if (field.arithmeticFields) {
        result = [...result, ...getAllFieldsWithReportConfig(field.arithmeticFields, selectedFields, operations)];
      }
    }
    return result;
  };

  // ==================== EXISTING ACCEPT FORMS LOGIC ====================

  const getUserDeskAssignments = (userData) => {
    try {
      if (userData.desk_assignments && userData.desk_assignments.length > 0) {
        const desks = userData.desk_assignments.map(d => ({
          id: d.id || d.desk_id || d,
          name: d.name || d.desk_name || `Desk ${d.id || d}`,
          desk_number: d.desk_number || d.id || d
        }));
        setUserDesks(desks);
        sessionStorage.setItem('desk_assignments', JSON.stringify(desks));
        return desks;
      }

      const storedDesks = sessionStorage.getItem('desk_assignments');
      if (storedDesks) {
        const desks = JSON.parse(storedDesks);
        setUserDesks(desks);
        return desks;
      }

      if (userData.desk_ids && userData.desk_ids.length > 0) {
        const desks = userData.desk_ids.map(id => ({ id, name: `Desk ${id}`, desk_number: id }));
        setUserDesks(desks);
        sessionStorage.setItem('desk_assignments', JSON.stringify(desks));
        return desks;
      }

      return [];
    } catch (error) {
      console.error("Error getting desk assignments:", error);
      return [];
    }
  };

  const getFormAssignedDesks = (form) => {
    try {
      if (!form.access_control) return [];

      let accessControl = form.access_control;
      if (typeof accessControl === 'string') {
        accessControl = JSON.parse(accessControl);
      }

      let selectedDesks = [];
      const formIdStr = String(form.id);

      if (accessControl[formIdStr] && accessControl[formIdStr].selectedDesks) {
        selectedDesks = accessControl[formIdStr].selectedDesks;
      }
      else if (accessControl.selectedDesks && Array.isArray(accessControl.selectedDesks)) {
        selectedDesks = accessControl.selectedDesks;
      }
      else if (Array.isArray(accessControl)) {
        selectedDesks = accessControl.map(item => item.id || item.desk_id || item);
      }
      else {
        for (let key in accessControl) {
          if (accessControl[key] && accessControl[key].selectedDesks) {
            selectedDesks = accessControl[key].selectedDesks;
            break;
          }
        }
      }

      return selectedDesks;
    } catch (e) {
      console.error('Error parsing access_control:', e);
      return [];
    }
  };

  useEffect(() => {
    const user = sessionStorage.getItem('user');
    const token = sessionStorage.getItem('token');

    if (!user || !token) {
      setError('Please login first');
      message.error('Please login first');
      navigate('/login');
      return;
    }

    try {
      const userData = JSON.parse(user);
      setCurrentUser(userData);

      const userRole = userData.role || userData.role_name;
      const userRoleId = userData.role_id;

      if (userRole !== 'DMA_DESK_OFFICER' && userRoleId !== 4 && userRoleId !== 6) {
        setError('You do not have permission to access this page');
        message.error('You do not have permission to access this page');
        navigate('/super-admin/dashboard');
        return;
      }

      const desks = getUserDeskAssignments(userData);

      if (desks.length === 0) {
        message.warning('No desks assigned to your account. Please contact administrator.');
        setError('No desks assigned. You cannot access any forms.');
      } else {
        const deskNames = desks.map(d => d.name || `Desk ${d.id}`).join(', ');
        message.info(`Accessing forms for ${desks.length} desk(s): ${deskNames}`);
      }

      fetchPendingForms(desks);
    } catch (error) {
      console.error('Error parsing user data:', error);
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('token');
      navigate('/login');
    }
  }, [navigate]);

 const fetchPendingForms = async (desks = null) => {
    setLoading(true);
    setError(null);

    let userDeskList = desks;
    if (!userDeskList || userDeskList.length === 0) {
        userDeskList = userDesks;
    }

    if (userDeskList.length === 0) {
        try {
            const userData = JSON.parse(sessionStorage.getItem('user') || '{}');
            userDeskList = userData.desk_assignments || userData.desk_ids?.map(id => ({ id })) || [];
        } catch (e) {
            console.error("Error parsing stored desks:", e);
        }
    }

    const userDeskIds = userDeskList.map(d => {
        if (typeof d === 'object') {
            return d.id || d.desk_id || d.desk_number;
        }
        return d;
    }).filter(id => id !== undefined && id !== null);

    if (userDeskIds.length === 0) {
        setForms([]);
        setLoading(false);
        message.info('No desks assigned to your account. Please contact admin.');
        return;
    }

    try {
        const token = sessionStorage.getItem('token');
        
        // ✅ Check if token exists
        if (!token) {
            throw new Error('No authentication token found');
        }
        
        console.log('Fetching pending forms with token:', token ? 'Token exists' : 'No token');
        console.log('API URL:', `${API_BASE}/forms/pending`);
        
        const response = await axios.get(`${API_BASE}/forms/pending`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            timeout: 30000 // 30 seconds timeout
        });

        console.log('API Response:', response.data);

        if (response.data && response.data.success) {
            let allForms = response.data.data || [];
            const accessControlMap = {};
            const matchedForms = [];

            allForms = allForms.map(form => ({
                ...form,
                access_control: typeof form.access_control === 'string' ? JSON.parse(form.access_control || '{}') : (form.access_control || {}),
                permissions: typeof form.permissions === 'string' ? JSON.parse(form.permissions || '{}') : (form.permissions || {}),
                report_config: typeof form.report_config === 'string' ? JSON.parse(form.report_config || '{}') : (form.report_config || {}),
                workflow: typeof form.workflow === 'string' ? JSON.parse(form.workflow || '{}') : (form.workflow || {}),
                sla: typeof form.sla === 'string' ? JSON.parse(form.sla || '{}') : (form.sla || {})
            }));

            allForms.forEach(form => {
                const formAssignedDesks = getFormAssignedDesks(form);
                accessControlMap[form.id] = {
                    desks: formAssignedDesks,
                    can_access: formAssignedDesks.length > 0,
                    can_edit: true,
                    can_view: true,
                    can_submit: true
                };

                if (formAssignedDesks.length === 0) return;

                const matchingDesks = formAssignedDesks.filter(deskId => userDeskIds.includes(deskId));

                if (matchingDesks.length > 0) {
                    matchedForms.push(form);
                }
            });

            setFormAccessControlMap(accessControlMap);
            setForms(matchedForms);

            const counts = {};
            userDeskIds.forEach(deskId => {
                counts[deskId] = matchedForms.filter(f => {
                    const formDesks = accessControlMap[f.id]?.desks || [];
                    return formDesks.includes(deskId);
                }).length;
            });
            setDeskFormsCount(counts);

            if (matchedForms.length === 0) {
                message.info(`No pending forms found for your assigned desks`);
            } else {
                message.success(`Found ${matchedForms.length} pending form(s) for your desks`);
            }
        } else {
            const errorMsg = response.data?.error || 'Failed to fetch forms';
            setError(errorMsg);
            message.error(errorMsg);
        }
    } catch (error) {
        console.error('Fetch pending forms error:', error);
        
        // ✅ Better error handling
        let errorMessage = 'Failed to fetch forms';
        
        if (error.response) {
            // Server responded with error status
            console.error('Error response:', error.response.status, error.response.data);
            
            if (error.response.status === 401) {
                errorMessage = 'Session expired. Please login again.';
                message.error(errorMessage);
                sessionStorage.removeItem('user');
                sessionStorage.removeItem('token');
                setTimeout(() => navigate('/login'), 1500);
            } else if (error.response.status === 403) {
                errorMessage = 'You do not have permission to view pending forms.';
                message.error(errorMessage);
            } else if (error.response.status === 404) {
                errorMessage = 'API endpoint not found. Please check if backend is running.';
                message.error(errorMessage);
            } else if (error.response.status === 500) {
                errorMessage = 'Server error. Please try again later.';
                message.error(errorMessage);
            } else {
                errorMessage = error.response.data?.error || `Server error: ${error.response.status}`;
                message.error(errorMessage);
            }
        } else if (error.request) {
            // Request was made but no response received
            console.error('No response received:', error.request);
            errorMessage = 'Network error: Unable to connect to server. Please check your connection.';
            message.error(errorMessage);
        } else {
            // Something else happened
            console.error('Error message:', error.message);
            errorMessage = `Error: ${error.message}`;
            message.error(errorMessage);
        }
        
        setError(errorMessage);
        setForms([]); // Clear forms on error
    } finally {
        setLoading(false);
    }
};

  const fetchSubmittedData = async (formId) => {
    setLoadingData(true);
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/forms/${formId}/submissions/latest`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data && response.data.data !== undefined) {
        setSubmittedData(response.data.data);
        setEditableValues(response.data.data || {});
        if (Object.keys(response.data.data).length === 0) {
          console.log('No submission data found for this form (expected for pending forms)');
        }
      } else {
        setSubmittedData({});
        setEditableValues({});
        console.log('No submission data available');
      }
    } catch (error) {
      console.error('Error fetching submitted data:', error);
      if (error.response?.status !== 404 && error.code !== 'ECONNABORTED') {
        message.warning('Could not load form submission data');
      }
      setSubmittedData({});
      setEditableValues({});
    } finally {
      setLoadingData(false);
    }
  };

  // ==================== UPDATED handleAccept - FIRE AND FORGET (NO AWAIT) ====================
  const handleAccept = async (formId) => {
    const formDesks = formAccessControlMap[formId]?.desks || [];
    const userDeskIds = userDesks.map(d => d.id || d);

    if (!formDesks.some(deskId => userDeskIds.includes(deskId))) {
        message.error('You do not have permission to approve this form');
        return;
    }

    try {
        const token = sessionStorage.getItem('token');
        
        const response = await axios.post(
            `${API_BASE}/forms/${formId}/approve`,
            {
                reviewed_by: currentUser?.id,
                reviewer_notes: reviewerNotes || null
            },
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            }
        );

        if (response.data.success) {
            const acceptedForm = forms.find(f => f.id === formId);
            
            // ✅ 1. Send notification to FORM CREATOR
            sendAcceptNotification(acceptedForm);
            
            // ✅ 2. Send notification to ASSIGNED ULBs - FIRE AND FORGET (NO await)
            notifyAssignedUlbsOnAccept(acceptedForm);  // await REMOVED

            message.success('Form approved successfully! Notifications are being sent in background.');

            setShowPreview(false);
            setReviewerNotes('');
            setSubmittedData(null);
            setEditableValues({});
            fetchPendingForms(userDesks);
        } else {
            message.error(response.data.error || 'Failed to approve form');
        }
    } catch (error) {
        console.error('Approve error:', error);
        if (error.response?.status === 401) {
            message.error('Session expired. Please login again.');
            sessionStorage.removeItem('user');
            sessionStorage.removeItem('token');
            navigate('/login');
        } else if (error.response?.status === 403) {
            message.error('You do not have permission to approve this form');
        } else {
            message.error(error.response?.data?.error || 'Failed to approve form');
        }
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      message.warning("Please provide rejection reason");
      return;
    }

    if (selectedForm) {
      const formDesks = formAccessControlMap[selectedForm.id]?.desks || [];
      const userDeskIds = userDesks.map(d => d.id || d);

      if (!formDesks.some(deskId => userDeskIds.includes(deskId))) {
        message.error('You do not have permission to reject this form');
        setShowRejectModal(false);
        return;
      }
    }

    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE}/forms/${selectedForm.id}/reject`,
        {
          reviewed_by: currentUser?.id,
          rejection_reason: rejectReason,
          reviewer_notes: reviewerNotes || null
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        sendRejectNotification(selectedForm, rejectReason);
        message.success('Form rejected successfully');
        setShowRejectModal(false);
        setRejectReason('');
        setReviewerNotes('');
        setShowPreview(false);
        setSubmittedData(null);
        setEditableValues({});
        fetchPendingForms(userDesks);
      } else {
        message.error(response.data.error || 'Failed to reject form');
      }
    } catch (error) {
      console.error('Reject error:', error);

      if (error.response?.status === 401) {
        message.error('Session expired. Please login again.');
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('token');
        navigate('/login');
      } else if (error.response?.status === 403) {
        message.error('You do not have permission to reject this form');
      } else {
        message.error(error.response?.data?.error || 'Failed to reject form');
      }
    }
  };

  const handlePreview = async (form) => {
    const formDesks = formAccessControlMap[form.id]?.desks || [];
    const userDeskIds = userDesks.map(d => d.id || d);
    const hasAccess = formDesks.some(deskId => userDeskIds.includes(deskId));

    if (!hasAccess) {
      message.error('You do not have permission to view this form');
      return;
    }

    const parsedForm = {
      ...form,
      access_control: typeof form.access_control === 'string' ? JSON.parse(form.access_control || '{}') : (form.access_control || {}),
      permissions: typeof form.permissions === 'string' ? JSON.parse(form.permissions || '{}') : (form.permissions || {}),
      report_config: typeof form.report_config === 'string' ? JSON.parse(form.report_config || '{}') : (form.report_config || {}),
      workflow: typeof form.workflow === 'string' ? JSON.parse(form.workflow || '{}') : (form.workflow || {}),
      sla: typeof form.sla === 'string' ? JSON.parse(form.sla || '{}') : (form.sla || {})
    };

    setSelectedForm(parsedForm);
    setPreviewActiveTab('form-data');
    setShowPreview(true);
    await fetchSubmittedData(form.id);
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const getFilteredForms = () => {
    if (deskFilter === 'all') return forms;
    const filterDeskId = parseInt(deskFilter);
    return forms.filter(form => {
      const formDesks = formAccessControlMap[form.id]?.desks || [];
      return formDesks.includes(filterDeskId);
    });
  };

  // ==================== RESTRICTIONS RENDERER ====================
  const renderRestrictions = () => {
    if (!selectedForm) return null;

    let accessControlData = selectedForm.access_control || {};
    if (typeof accessControlData === 'string') {
      try {
        accessControlData = JSON.parse(accessControlData);
      } catch(e) {
        accessControlData = {};
      }
    }

    const hasAnyPermissions = Object.keys(accessControlData).length > 0;

    if (!hasAnyPermissions) {
      return (
        <Empty
          description="No restrictions configured for this form"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      );
    }

    const getRoleName = (roleId) => {
      const roleMap = {
        '1': 'ULB',
        '2': 'DISTRICT ADMIN',
        '3': 'DIVISIONAL OFFICER',
        '7': 'ULB CHIEF OFFICER',
        '100': 'DESK'
      };
      return roleMap[String(roleId)] || `Role ${roleId}`;
    };

    const getUlbName = (id) => {
      return ulbMap[id] || `ULB ${id}`;
    };

    return (
      <div>
        <Descriptions bordered column={1} size="small">
          {Object.entries(accessControlData)
            .filter(([_, perm]) => {
              return perm.can_access === true ||
                     (perm.selectedIds && perm.selectedIds.length > 0) ||
                     (perm.selectedDesks && perm.selectedDesks.length > 0);
            })
            .map(([roleId, perm]) => {
              const selectedIds = perm.selectedIds || perm.selectedItems || [];
              const selectedDesks = perm.selectedDesks || [];
              const hasSelectedIds = selectedIds.length > 0;
              const hasSelectedDesks = selectedDesks.length > 0;
              const roleName = getRoleName(roleId);
              const showAll = showAllUlbs[roleId] || false;
              const displayLimit = 20;
              const visibleIds = showAll ? selectedIds : selectedIds.slice(0, displayLimit);
              const hasMore = selectedIds.length > displayLimit;

              return (
                <Descriptions.Item key={roleId} label={roleName}>
                  <div>
                    <Tag color={perm.can_access ? 'green' : 'orange'}>
                      {perm.can_access ? '✓ Access Granted' : '⚠️ Access Not Granted'}
                    </Tag>

                    {perm.can_view && <Tag color="blue" icon={<EyeOutlined />}>View</Tag>}
                    {perm.can_edit && <Tag color="orange" icon={<EditOutlined />}>Edit</Tag>}
                    {perm.can_submit && <Tag color="cyan" icon={<CheckCircleOutlined />}>Submit</Tag>}

                    {hasSelectedIds && (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
                          <Text type="secondary">Selected ULBs ({selectedIds.length}):</Text>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                          {visibleIds.map(id => {
                            const ulbName = getUlbName(id);
                            return (
                              <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <Tag color="geekblue" style={{ margin: 0 }}>
                                  🏛️ {ulbName}
                                </Tag>
                              </div>
                            );
                          })}
                        </div>
                        {hasMore && (
                          <Button type="link" size="small" onClick={() => toggleShowAllUlbs(roleId)}>
                            {showAll ? 'View Less' : `View More (+${selectedIds.length - displayLimit})`}
                          </Button>
                        )}
                      </div>
                    )}

                    {hasSelectedDesks && (
                      <div style={{ marginTop: 8 }}>
                        <Text type="secondary">Selected Desks ({selectedDesks.length}):</Text>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                          {selectedDesks.map(desk => (
                            <Tag key={desk} color="purple" icon={<DesktopOutlined />}>Desk {desk}</Tag>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </Descriptions.Item>
              );
            })}
        </Descriptions>
      </div>
    );
  };

  // ==================== WORKFLOW RENDERER ====================
  const renderWorkflow = () => {
    if (!selectedForm) return null;

    const workflow = selectedForm.workflow || {};
    const workflowType = workflow.type || 'none';
    const sequence = workflow.sequence || [];

    if (workflowType === 'none' && sequence.length === 0) {
      return (
        <Empty
          description="No workflow configured for this form"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      );
    }

    return (
      <div>
        <Descriptions bordered column={1} size="small" style={{ marginBottom: 16 }}>
          <Descriptions.Item label="Workflow Type">
            <Tag color={workflowType === 'standard' ? 'blue' : 'purple'}>
              {workflowType === 'standard' ? 'Standard Workflow' : 'Custom Workflow'}
            </Tag>
          </Descriptions.Item>

          <Descriptions.Item label="Approval Sequence">
            {sequence.length > 0 ? (
              <Steps
                current={-1}
                direction="vertical"
                size="small"
                items={sequence.map((role, index) => ({
                  title: role,
                  description: `Approval step ${index + 1}`,
                  icon: <SafetyOutlined />,
                  status: 'wait'
                }))}
              />
            ) : (
              <Text type="secondary">No sequence defined</Text>
            )}
          </Descriptions.Item>
        </Descriptions>
      </div>
    );
  };

  // ==================== SLA RENDERER ====================
  const renderSla = () => {
    if (!selectedForm) return null;

    const sla = selectedForm.sla || {};

    if (Object.keys(sla).length === 0) {
      return (
        <Empty
          description="No SLA configured for this form"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      );
    }

    const workflow = selectedForm.workflow || {};
    const sequence = workflow.sequence || [];

    return (
      <div>
        <Descriptions bordered column={1} size="small">
          {Object.entries(sla).map(([roleId, slaLimit]) => {
            const roleName = sequence.find(r => r === roleId) || roleId;
            return (
              <Descriptions.Item key={roleId} label={`SLA for ${roleName}`}>
                <Space>
                  <Text strong>{slaLimit.value}</Text>
                  <Text>{slaLimit.unit}</Text>
                  <Tag color="green">Days</Tag>
                </Space>
              </Descriptions.Item>
            );
          })}
        </Descriptions>
      </div>
    );
  };

  const columns = [
    {
      title: "Form Name",
      dataIndex: "form_name",
      key: "form_name",
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text || 'Untitled Form'}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <UserOutlined /> {record.created_by_name} •
            <CalendarOutlined /> {record.created_at ? new Date(record.created_at).toLocaleDateString() : 'N/A'}
          </Text>
        </Space>
      ),
    },
    {
      title: "Assigned Desk",
      key: "assigned_desk",
      render: (_, record) => {
        const formDesks = formAccessControlMap[record.id]?.desks || [];
        const matchingDesks = formDesks.filter(deskId =>
          userDesks.map(d => d.id || d).includes(deskId)
        );

        return (
          <Tag color="purple" icon={<DesktopOutlined />}>
            {matchingDesks.map(id => {
              const desk = userDesks.find(d => (d.id || d) === id);
              return desk?.name || `Desk ${id}`;
            }).join(', ') || 'No matching desk'}
          </Tag>
        );
      },
    },
    {
      title: "Entry Type",
      dataIndex: "entry_type",
      key: "entry_type",
      render: (type) => (
        <Tag color={type === 'multi' ? 'purple' : 'blue'}>
          {type === 'multi' ? 'Multi Entry' : 'Single Entry'}
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "approval_status",
      key: "status",
      render: (status) => (
        <Badge
          status={status === 'approved' ? 'success' : status === 'rejected' ? 'error' : 'warning'}
          text={status?.toUpperCase() || 'PENDING'}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 350,
      render: (_, record) => (
        <Space>
          <Tooltip title="Preview & Edit Form Data">
            <Button icon={<EyeOutlined />} onClick={() => handlePreview(record)}>
              Preview & Edit
            </Button>
          </Tooltip>
          {record.approval_status === 'pending' && (
            <>
              <Tooltip title="Accept Form">
                <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => handleAccept(record.id)} style={{ background: "#52c41a" }}>
                  Accept
                </Button>
              </Tooltip>
              <Tooltip title="Reject Form">
                <Button danger icon={<CloseCircleOutlined />} onClick={() => { setSelectedForm(record); setShowRejectModal(true); }}>
                  Reject
                </Button>
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  const filteredForms = getFilteredForms();
  const userDeskIds = userDesks.map(d => d.id || d);

  return (
    <>
     <Navbar />
    <div style={{ padding: 24, minHeight: '100vh', background: '#f0f2f5' }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap' }}>
          <div>
            <Title level={4}>
              <ApiOutlined style={{ marginRight: 8, color: '#1890ff' }} />
              Accept Forms
            </Title>
            <Text type="secondary">Review and approve forms submitted to your assigned desks</Text>
          </div>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={handleGoBack}>Back</Button>
            <Button icon={<HistoryOutlined />} onClick={() => fetchPendingForms(userDesks)} loading={loading}>Refresh</Button>
          </Space>
        </div>

        {userDesks.length > 0 && (
          <Alert
            message={
              <Space>
                <DesktopOutlined />
                <span>Your Assigned Desks: <strong>{userDesks.map(d => d.name || `Desk ${d.id}`).join(', ')}</strong></span>
              </Space>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {userDesks.length > 1 && forms.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Space wrap>
              <Button
                type={deskFilter === 'all' ? 'primary' : 'default'}
                onClick={() => setDeskFilter('all')}
              >
                All Desks ({forms.length})
              </Button>
              {userDesks.map(desk => {
                const deskId = desk.id || desk;
                const deskName = desk.name || `Desk ${deskId}`;
                const count = deskFormsCount[deskId] || 0;
                return (
                  <Button
                    key={deskId}
                    type={deskFilter === String(deskId) ? 'primary' : 'default'}
                    onClick={() => setDeskFilter(String(deskId))}
                    icon={<DesktopOutlined />}
                  >
                    {deskName} ({count})
                  </Button>
                );
              })}
            </Space>
          </div>
        )}

        {error && (
          <Alert message="Error" description={error} type="error" showIcon style={{ marginBottom: 16 }} closable />
        )}

        <Table
          columns={columns}
          dataSource={filteredForms}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: loading ? <Spin /> : 'No pending forms found for your desks' }}
        />
      </Card>

      {/* Preview Modal */}
      <Modal
        open={showPreview}
        onCancel={() => {
          setShowPreview(false);
          setSelectedForm(null);
          setReviewerNotes('');
          setSubmittedData(null);
          setEditableValues({});
          setEditableFileList({});
          setPreviewActiveTab('form-data');
        }}
        footer={null}
        width={1100}
        centered
        title={
          <Space>
            <FileTextOutlined style={{ color: '#1890ff' }} />
            <span>Form: {selectedForm?.form_name || 'Untitled Form'}</span>
            <Badge
              status={selectedForm?.approval_status === 'approved' ? 'success' : selectedForm?.approval_status === 'rejected' ? 'error' : 'warning'}
              text={selectedForm?.approval_status?.toUpperCase() || 'PENDING'}
            />
          </Space>
        }
      >
        {selectedForm && (
          <>
            <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Form ID">{selectedForm.id}</Descriptions.Item>
              <Descriptions.Item label="Entry Type">
                <Tag color={selectedForm.entry_type === 'multi' ? 'purple' : 'blue'}>
                  {selectedForm.entry_type === 'multi' ? 'Multi Entry' : 'Single Entry'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Assigned Desk" span={2}>
                <Tag color="purple" icon={<DesktopOutlined />}>
                  {formAccessControlMap[selectedForm.id]?.desks?.filter(d => userDeskIds.includes(d)).map(id => {
                    const desk = userDesks.find(d => (d.id || d) === id);
                    return desk?.name || `Desk ${id}`;
                  }).join(', ') || 'No matching desk'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Submission Date" span={2}>
                {submittedData?.submitted_at ? new Date(submittedData.submitted_at).toLocaleString() : 'Not submitted yet'}
              </Descriptions.Item>
            </Descriptions>

            <Tabs activeKey={previewActiveTab} onChange={setPreviewActiveTab} type="card">
              <TabPane
                tab={
                  <span>
                    <FileTextOutlined />
                    Form Data (Editable)
                  </span>
                }
                key="form-data"
              >
                <Divider orientation="left" style={{ marginTop: 0 }}>
                  <Space>
                    <EditOutlined />
                    <span>Editable Form Data with Validation (Changes are NOT saved)</span>
                  </Space>
                </Divider>

                {loadingData ? (
                  <div style={{ textAlign: 'center', padding: 50 }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 16 }}>Loading form data...</div>
                  </div>
                ) : (
                  renderEditableFormPreview()
                )}
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <SafetyOutlined />
                    Access Control
                  </span>
                }
                key="restrictions"
              >
                <Divider orientation="left" style={{ marginTop: 0 }}>
                  <Space>
                    <SafetyOutlined />
                    <span>Access Control & Restrictions (Read Only)</span>
                  </Space>
                </Divider>
                {renderRestrictions()}
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <SwapOutlined />
                    Workflow
                  </span>
                }
                key="workflow"
              >
                <Divider orientation="left" style={{ marginTop: 0 }}>
                  <Space>
                    <SwapOutlined />
                    <span>Approval Workflow (Read Only)</span>
                  </Space>
                </Divider>
                {renderWorkflow()}
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <ClockCircleOutlined />
                    SLA
                  </span>
                }
                key="sla"
              >
                <Divider orientation="left" style={{ marginTop: 0 }}>
                  <Space>
                    <ClockCircleOutlined />
                    <span>SLA Configuration (Read Only)</span>
                  </Space>
                </Divider>
                {renderSla()}
              </TabPane>
            </Tabs>

            <Divider orientation="left">Reviewer Notes</Divider>
            <TextArea
              rows={3}
              value={reviewerNotes}
              onChange={(e) => setReviewerNotes(e.target.value)}
              placeholder="Add review notes (optional) - These will be saved with the approval/rejection..."
            />

            <Divider />
            <div style={{ textAlign: 'right' }}>
              <Space>
                <Button onClick={() => {
                  setShowPreview(false);
                  setReviewerNotes('');
                  setSubmittedData(null);
                  setEditableValues({});
                  setEditableFileList({});
                  setPreviewActiveTab('form-data');
                }}>
                  Close
                </Button>
                {selectedForm.approval_status === 'pending' && (
                  <>
                    <Button
                      danger
                      onClick={() => {
                        setShowPreview(false);
                        setShowRejectModal(true);
                      }}
                    >
                      Reject
                    </Button>
                    <Button
                      type="primary"
                      onClick={() => handleAccept(selectedForm.id)}
                      style={{ background: "#52c41a" }}
                    >
                      Approve
                    </Button>
                  </>
                )}
              </Space>
            </div>
          </>
        )}
      </Modal>

      {/* Reject Modal */}
      <Modal
        open={showRejectModal}
        onCancel={() => {
          setShowRejectModal(false);
          setRejectReason('');
          setReviewerNotes('');
        }}
        title={
          <Space>
            <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
            <span>Reject Form</span>
          </Space>
        }
        centered
        footer={[
          <Button key="cancel" onClick={() => setShowRejectModal(false)}>Cancel</Button>,
          <Button key="submit" type="primary" danger onClick={handleReject}>Reject Form</Button>,
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>Form: </Text>
          <Text>{selectedForm?.form_name}</Text>
        </div>
        <div style={{ marginBottom: 12 }}>
          <Text strong>Rejection Reason <Text type="danger">*</Text></Text>
          <TextArea
            rows={4}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Please provide a detailed reason for rejection..."
          />
        </div>
        <div>
          <Text strong>Additional Notes (Optional)</Text>
          <TextArea
            rows={2}
            value={reviewerNotes}
            onChange={(e) => setReviewerNotes(e.target.value)}
            placeholder="Any additional comments for the form submitter..."
          />
        </div>
      </Modal>
    </div>
     </>
  );
};

export default AcceptForms;